/**
 * Contas a Receber — mecânica simplificada (mockups aprovados em 23/10/2026).
 *  - Duas abas sobre os MESMOS registros: "Todos os recebíveis" (comissões e avulsos: Operação, Tipo, Pagador, Origem,
 *    Valor, Previsão, Situação; Previsto a receber · Recebido; "Recebível avulso") e "Comissões" (NF do parceiro,
 *    pedido interno, busca ampla; Previsto a receber · Disponível para resgate). O que ainda não foi faturado fica à
 *    parte ("Ainda não faturado · previsões e backlog"), fora dos totais, sem vencimento inventado.
 *  - As comissões nascem sozinhas dos fatos de origem (nota do parceiro, remessa, OL, OTI): não há abertura manual.
 *  - Ficha da comissão: origem rastreável (oportunidade → proposta → pedido → OCF → NF → comissão → parcelas), cartões
 *    estimado · confirmado · disponível para resgate, condição de liberação e parcelas com "Atualizar" (pagamento do
 *    Cliente e/ou confirmação do valor numa só janela). Histórico, composição e condições ficam recolhidos.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { navegar } from '../rotas.ts';
import { Abas, Ajuda, CampoBusca, CardLista, CardsLista, EstadoCarga, Modal, RolagemIndicada, useCarga } from '../comercial/ui.tsx';
import { CampoData, hojeIso } from '../comercial/CampoData.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { centavosDe, dataBr, novaChave, plural, rs } from './ContasPagar.tsx';
import { EmitirNota, NovoAvulso, Situacao, type PermissoesReceber } from './ReceberDocumentos.tsx';
import {
  MARCOS_LIBERACAO,
  MARCOS_REPASSE,
  MODALIDADES,
  ORIGENS_CONFIRMACAO,
  AJUDA_RECONHECIMENTO,
  RECONHECIMENTO,
  ROTULO_ORIGEM,
  textoLiberacao,
  textoRepasse,
  TIPOS_LIBERACAO,
  type CampoLiberacao,
  type CampoRepasse,
  type CondicaoOperacao,
  type Modalidade,
  type OrigemCampo,
  type SituacaoLiberacao,
  type SituacaoRepasse,
  type TipoDias,
  diasEntre,
  ORIGENS_CALENDARIO,
  ORIGENS_VALOR_CALENDARIO,
  ORIGENS_REDISTRIBUICAO,
  PERIODICIDADES_CALENDARIO,
  ROTULO_ORIGEM_DISTRIBUICAO,
  TRATAMENTOS_PRAZO,
  type PeriodicidadeCalendario,
  type TratamentoPrazo,
} from '../../../backend/src/compartilhado/receber.ts';

// ---------------------------------------------------------------- tipos da API
type Disp = 'liberado_aguardando_confirmacao' | 'disponivel_nota' | null;
type Composicao = { parte: string; margem: string; frete: string; baseAposFrete: string; estimativa: string | null };
interface SituacaoLinha {
  codigo: string;
  rotulo: string;
  tom: string;
}
/** Próxima parcela em aberto: data esperada de recebimento (estimada ou efetiva), o que falta e a causa de vencida. */
interface ProximaPrevisao {
  data: string | null;
  estimada: boolean;
  /** prazo negociado (informado) × data calculada */
  origem: 'negociada' | 'calculada';
  calculada: string | null;
  rotulo: string;
  falta: string[];
  vencida: string | null;
  abertas: number;
}
interface LinhaTodos {
  tipo: 'comissao' | 'avulso';
  numero: string;
  tipoRotulo: string;
  pagador: string;
  origem: string;
  valor: string | null;
  situacaoValor: string;
  aReceber: string | null;
  previsao: string | null;
  proximaPrevisao: ProximaPrevisao | null;
  situacao: SituacaoLinha;
  busca: string;
}
interface LinhaComissao {
  numero: string;
  modalidade: Modalidade;
  modalidadeRotulo: string;
  cliente: string;
  parceiro: string;
  pedido: string;
  ocf: string | null;
  documento: string | null;
  nf: string | null;
  nfData: string | null;
  pedidoInterno: string | null;
  origem: string;
  valor: string | null;
  situacaoValor: string;
  aReceber: string | null;
  disponivel: string;
  /** E0: sem as parcelas com resgate ativo (D14) */
  disponivelResgate: string;
  solicitado: string | null;
  emVerificacao: { valor: string | null; motivo: string } | null;
  cancelada: boolean;
  parceiroId: string;
  parcelas: { item: string; rotulo: string; valor: string | null; quitado: string; recebidoCaixa: string; aReceber: string | null; data: string | null; disponivel: string; solicitado: string | null; resgate: string | null; notas: string[] }[];
  previsao: string | null;
  proximaPrevisao: ProximaPrevisao | null;
  itens: number;
  situacao: SituacaoLinha;
  alerta: string | null;
  pendenciaEstimativa: string | null;
  busca: string;
}
interface Permissoes extends PermissoesReceber {
  ajustarCondicao: boolean;
  redistribuir: boolean;
  registrarPagamento: boolean;
  estornarPagamento: boolean;
  confirmarValor: boolean;
  cancelarComissao: boolean;
  completarLegado: boolean;
  solicitarResgate: boolean;
}
interface Painel {
  hoje: string;
  /** primeiro mês do histórico financeiro (os meses do filtro começam nele) */
  inicioHistorico: string | null;
  parceiros: { id: string; nome: string }[];
  recebidos: {
    recebimento: string;
    tipo: 'recebimento' | 'credito_aplicado';
    data: string;
    nota: string | null;
    origem: { tipo: 'comissao' | 'avulso'; numero: string; comissaoId: string | null; parceiroId: string | null; recebivelId: string | null } | null;
    dinheiro: string;
    ir: string;
    antecipacao: string;
    credito: string;
    excedente: string;
    estornado: boolean;
    origemSituacao: 'cancelada' | 'em_verificacao' | null;
  }[];
  permissoes: Permissoes;
  todos: { resumo: { previsto: string | null; semValor: number; recebido: string }; linhas: LinhaTodos[]; recebimentos: { data: string; valor: string }[] };
  comissoes: { resumo: { previsto: string | null; semValor: number; disponivel: string }; linhas: LinhaComissao[] };
  naoFaturado: { total: string | null; semValor: number; linhas: { pedido: string; modalidade: Modalidade; parceiro: string; cliente: string; documento: string | null; ocfs: string[]; estimativa: string | null; motivo: string; pendenciaEstimativa: string | null }[] };
}
interface Item {
  id: string;
  ordem: number;
  total: number;
  chave: string;
  parcela: string;
  rotulo: string;
  base: string | null;
  estimado: string | null;
  vencimentoParcela: string | null;
  vencimentoOrigem: string | null;
  reconhecimentoPagamento: { id: string; data: string; origem: string; por: string | null; em: string } | null;
  resgate: { numero: string; data: string } | null;
  previsao: {
    original: string | null;
    atual: string | null;
    origemAtual: 'negociada' | 'calculada';
    negociada: { data: string; motivo: string; em: string; por: string | null } | null;
    calculada: string | null;
    calculoAposNegociado: { data: string | null; motivo: string } | null;
    estimada: boolean;
    projecao: {
      vencimentoCliente: string | null;
      pagamentoCliente: { data: string | null; estimada: boolean; parcial: boolean };
      liberacao: { data: string | null; estimada: boolean };
      reconhecimento: { data: string | null; estimada: boolean } | null;
      resgate: { data: string | null; estimada: boolean } | null;
      recebimento: { data: string | null; estimada: boolean; efetivo: boolean };
      fundamento: string;
      falta: string[];
      vencida: string | null;
    };
    replanejamentos: { previsao: string; motivo: string; em: string; por: string | null }[];
    historico: { previsao: string | null; falta: string | null; fundamento: string; motivo: string; em: string; origem: 'automatica' | 'negociada' | 'retorno_calculo'; por: string | null }[];
  };
  confirmado: { id: string; valor: string; data: string; origem: string; por: string | null; em: string; evidencia: string | null } | null;
  confirmacoes: { valor: string; data: string; origem: string; por: string | null; em: string; justificativa: string | null }[];
  liberacao: { situacao: SituacaoLiberacao; data: string | null; fato: string; registradoEm: string | null; atrasoCliente: number | null; pagoCent: number; condicao: CampoLiberacao };
  repasse: { situacao: SituacaoRepasse; vencimento: string | null; detalhe: string; condicao: CampoRepasse };
  disponibilidade: Disp;
  saldoNota: string;
  cobranca: { cobrado: string; quitado: string; recebidoCaixa: string; notas: { numero: string; numeroFiscal: string; situacao: string; valor: string }[]; situacao: 'nao_cobrado' | 'em_nota' | 'nota_cancelada' | 'quitado' };
}
interface ParcelaCalendario {
  prazoDias: number | null;
  vencimento: string;
  valor: string;
  origemValor: 'documentado' | 'sugestao_igual';
  /** só no informado: a data que a sugestão trazia, quando foi editada */
  vencimentoSugerido?: string;
}
interface Ficha {
  id: string;
  numero: string;
  modalidade: Modalidade;
  origemTipo: 'nota_parceiro' | 'remessa' | 'projeto' | 'oti';
  ocf: string | null;
  nf: string | null;
  nfData: string | null;
  pedidoInterno: string | null;
  pedidoInternoOrigem: 'financeiro' | 'ocf' | null;
  pedidoInternoDivergencia: string | null;
  referencias: { pedidoInterno: string; em: string; por: string | null }[];
  calendario: {
    condicao: string;
    condicaoId: string | null;
    prazoGeral: string | null;
    valorFaturado: string | null;
    dataNf: string | null;
    congelado: { nome: string; dias: number[]; copiadoEm: string } | null;
    informado: { origem: string; documento: string | null; motivo: string; em: string; por: string | null; parcelas: number; forma: string | null; detalhe: ParcelaCalendario[] | null; conferido: boolean | null } | null;
    estruturado: boolean;
    falta: string[];
    referenciaListaAtual: { nome: string; dias: number[] } | null;
    observacaoCondicao: string | null;
    envioProposta: string | null;
    sugestao: { fonte: 'condicao_envio' | 'cadastro_atual'; rotulo: string; documento: string; condicao: { id: string; nome: string; dias: number[] }; aviso: string | null; parcelas: ParcelaCalendario[] } | null;
    semSugestao: string | null;
  } | null;
  /** E0: o que o calendário pode fazer nesta comissão (e por quê), antes de abrir a janela */
  diagnosticoCalendario: {
    bloqueio: { motivo: string; acao: string } | null;
    parcelas: { item: string; rotulo: string; base: string | null; vencimento: string | null; motivos: string[] }[];
    podeDividir: boolean;
    podeCompletarVencimento: boolean;
    valorParcelaUnica: string | null;
    limitacaoProporcional: string | null;
    mensagem: string | null;
  };
  origemDesfeita: string | null;
  vinculosOrigemDesfeita: string[];
  alertaRefaturamento: string | null;
  cancelamento: { justificativa: string; em: string; por: string | null } | null;
  oportunidadeId: string;
  oportunidade: string;
  propostaId: string;
  pedido: string;
  pedidoCancelado: boolean;
  documento: string | null;
  parceiroId: string;
  parceiro: string;
  cliente: string;
  proposta: string;
  versao: number;
  estimativa: string | null;
  estimativaAtual: string | null;
  base: Record<string, unknown> & { avisos?: string[]; pendenciaEstimativa?: string | null; margem?: string; frete?: string; baseAposFrete?: string; abatimento?: number | null; valorFaturado?: string; comissaoBruta?: string; mensalidade?: string; percentual?: number; totalContrato?: string; avaliacao?: string; oferta?: string };
  abertaEm: string;
  condicao: CondicaoOperacao;
  condicoes: { id: string; tipo: string; condicao: CondicaoOperacao; justificativa: string | null; em: string; por: string | null }[];
  pagamentos: { id: string; parcela: string; data: string; valor: string; origem: string; evidencia: string | null; observacao: string | null; registradoEm: string; por: string | null; estorno: { justificativa: string; em: string; por: string | null } | null }[];
  distribuicoes: { id: string; tipo: string; motivo: string | null; origem: string | null; documento?: string | null; dataInfo: string | null; em: string; por: string | null; editadas?: { sugerido: string; vencimento: string }[] }[];
  itens: Item[];
  parcelas: { item: string; chave: string; rotulo: string; base: string | null; vencimento: string | null; pago: string }[];
  resumo: LinhaComissao;
  operacao: { estimativa: string | null; backlog: string | null; composicao: Composicao[] | null; comissaoAposFrete: string | null; abatimento: number | null } | null;
  irmas: { numero: string; nf: string | null; nfData: string | null }[];
  reconhecimentos: { id: string; parcela: string; data: string; origem: string; registradoEm: string; por: string | null; estorno: { justificativa: string; em: string; por: string | null } | null }[];
  resgates: { numero: string; data: string; meio: string | null; observacao: string | null; em: string; por: string | null; itens: { item: string; comissao: string }[]; cancelamento: { justificativa: string; em: string } | null }[];
  sugestoes: { id: string; campo: 'liberacao' | 'repasse' | 'confirmacao'; origem: string; detalhe: string; valor: Record<string, unknown>; texto: string }[];
  hoje: string;
  permissoes: Permissoes;
}
interface Retrato {
  estimado: string | null;
  confirmado: string | null;
  liberacao: SituacaoLiberacao;
  liberacaoData: string | null;
  liberacaoFato: string;
  registradoEm: string | null;
  repasse: SituacaoRepasse;
  repasseVencimento: string | null;
  repasseDetalhe: string;
  disponibilidade: Disp;
  condicaoLiberacao: string;
  condicaoRepasse: string;
  previsao: string | null;
}
interface Efeito {
  id: string;
  rotulo: string;
  parcela: string;
  novo: boolean;
  mudou: boolean;
  antes: Retrato | null;
  depois: Retrato;
  protegido?: { repasseAplicado: boolean } | null;
  liberadoAntes?: boolean;
}

// ---------------------------------------------------------------- apresentação
const valorOuConfirmar = (v: string | null) => (v === null ? 'A confirmar' : rs(v));
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const ORIGEM_PAGAMENTO: Record<string, string> = { sistema_distribuidor: 'Sistema do distribuidor', informado_parceiro: 'Informação do parceiro', comprovante_cliente: 'Comprovante do Cliente', outra: 'Outra' };
const ORIGEM_CONF: Record<string, string> = Object.fromEntries(ORIGENS_CONFIRMACAO.map((o) => [o.id, o.rotulo]));
const ORIGEM_REC: Record<string, string> = { informado_parceiro: 'Informado pelo parceiro', sistema_distribuidor: 'Sistema do distribuidor', demonstrativo: 'Demonstrativo do parceiro', outra: 'Outra' };
/** Origem COMUM dos fatos marcados em "Atualizar parcela"; cada fato continua com o seu domínio de origens (e a sua tabela). */
const ORIGEM_COMUM: Record<string, string> = { informado_parceiro: 'Informado pelo parceiro', sistema_distribuidor: 'Sistema do distribuidor', comprovante_cliente: 'Comprovante do Cliente', demonstrativo: 'Demonstrativo do parceiro', informacao_solicitada: 'Informação solicitada ao parceiro', outra: 'Outra' };
/** A origem comum vale para o fato quando ele a aceita (informado × solicitada são a mesma informação do parceiro); senão, o fato pede a sua. */
function origemDoFato(comum: string, fato: 'pagamento' | 'reconhecimento' | 'confirmacao'): string | null {
  const aceitas = fato === 'pagamento' ? ORIGEM_PAGAMENTO : fato === 'reconhecimento' ? ORIGEM_REC : ORIGEM_CONF;
  if (comum in aceitas) return comum;
  if (comum === 'informacao_solicitada' && 'informado_parceiro' in aceitas) return 'informado_parceiro';
  if (comum === 'informado_parceiro' && 'informacao_solicitada' in aceitas) return 'informacao_solicitada';
  return null;
}
/** Origem de um fato: a comum (com "usar outra origem neste fato") ou a própria, quando a comum não se aplica. */
function OrigemFato({ fato, comum, propria, aoMudar, teste }: { fato: 'pagamento' | 'reconhecimento' | 'confirmacao'; comum: string; propria: string | null; aoMudar: (v: string | null) => void; teste: string }) {
  const opcoes = fato === 'pagamento' ? ORIGEM_PAGAMENTO : fato === 'reconhecimento' ? ORIGEM_REC : ORIGEM_CONF;
  const herdada = origemDoFato(comum, fato);
  if (propria === null && herdada)
    return (
      <p className="rc-sec sem-margem" data-teste={`${teste}-comum`}>
        Origem: {opcoes[herdada]} (comum) ·{' '}
        <button type="button" className="botao-link" onClick={() => aoMudar(herdada)} data-teste={`${teste}-outra`}>
          usar outra origem neste fato
        </button>
      </p>
    );
  return (
    <Campo rotulo={herdada ? 'Origem neste fato (diferente da comum)' : 'Origem neste fato (a comum não se aplica)'}>
      {(id) => (
        <select id={id} value={propria ?? ''} onChange={(e) => aoMudar(e.target.value || null)} data-teste={teste}>
          {!propria && <option value="">Escolha…</option>}
          {Object.entries(opcoes).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      )}
    </Campo>
  );
}
const selo = (texto: string, tom: 'ok' | 'pend' | 'ni' | 'ext') => <span className={`selo-previa ${tom}`}>{texto}</span>;
const Pendencia = ({ motivo }: { motivo: string | null | undefined }) =>
  motivo ? (
    <span className="rc-l2" data-teste="estimativa-pendente">
      <span className="selo-previa pend">Estimativa pendente</span>
      <Ajuda titulo="Estimativa pendente">{`Não há valor estimado: ${motivo}. Nada é distribuído por suposição; a estimativa aparece quando o dado existir.`}</Ajuda>
    </span>
  ) : null;
/** Situação de UMA parcela da comissão (cores coerentes em todo o Receber). */
function situacaoParcela(i: Item, pagoCent: number, baseCent: number | null): { rotulo: string; tom: string } {
  if (i.cobranca.situacao === 'quitado') return { rotulo: 'Quitada', tom: 'verde' };
  if (i.cobranca.situacao === 'nota_cancelada') return { rotulo: `Nota ${i.cobranca.notas.find((n) => n.situacao === 'cancelada')?.numero ?? ''} cancelada · aguardando providência`, tom: 'vermelho' };
  if (i.cobranca.situacao === 'em_nota') return { rotulo: `Em nota ${i.cobranca.notas.find((n) => n.situacao !== 'cancelada' && n.situacao !== 'substituida')?.numero ?? ''}`, tom: 'azul' };
  if (i.disponibilidade === 'disponivel_nota') return { rotulo: 'Disponível para resgate', tom: 'verde' };
  if (i.disponibilidade === 'liberado_aguardando_confirmacao') return { rotulo: 'Liberada · confirmar valor', tom: 'roxo' };
  if (i.liberacao.situacao === 'aguardando_liberacao') return { rotulo: i.liberacao.data ? `Aguardando prazo · ${dataBr(i.liberacao.data)}` : 'Aguardando marco', tom: 'amarelo' };
  if (i.liberacao.situacao === 'a_confirmar') return { rotulo: 'Liberação a confirmar', tom: 'cinza' };
  if (pagoCent > 0 && baseCent !== null && pagoCent < baseCent) return { rotulo: 'Pagamento parcial', tom: 'amarelo' };
  if (i.liberacao.atrasoCliente) return { rotulo: `Atraso do cliente · ${plural(i.liberacao.atrasoCliente, 'dia', 'dias')}`, tom: 'vermelho' };
  return { rotulo: 'Aguardando cliente', tom: 'amarelo' };
}

// ---------------------------------------------------------------- lista (Todos os recebíveis · Comissões)
const CHAVE_ABA = 'inttrix:receber:aba';
const CHAVE_PARCEIRO = 'inttrix:receber:parceiro';
/**
 * E0 (D15/D25): período = "Todo o período", um MÊS (do início do histórico financeiro até 12 meses à frente; nada fixo
 * no código) ou os recortes relativos. Cada cartão diz o seu recorte: Previsto pela data esperada (em aberto hoje);
 * Recebido pela data efetiva; Disponível e Resgate solicitado são sempre a posição de HOJE (o período não se aplica).
 */
type Periodo = string;
const MESES_NOME = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const MESES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesExtenso = (m: string) => `${MESES_NOME[Number(m.slice(5, 7)) - 1]} de ${m.slice(0, 4)}`.replace(/^./, (c) => c.toUpperCase());
const mesCurto = (m: string) => `${MESES_CURTO[Number(m.slice(5, 7)) - 1]}/${m.slice(0, 4)}`;
const somarMes = (m: string, n: number) => {
  const t = Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1 + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};
/** Meses do filtro: do início do histórico financeiro (calculado no servidor) até 12 meses depois do mês atual. */
function mesesDoFiltro(inicio: string | null, hoje: string) {
  const atual = hoje.slice(0, 7);
  const ini = inicio && inicio < atual ? inicio : atual;
  const anteriores: string[] = [];
  for (let m = somarMes(atual, -1); m >= ini; m = somarMes(m, -1)) anteriores.push(m);
  const seguintes = Array.from({ length: 12 }, (_, i) => somarMes(atual, i + 1));
  return { atual, anteriores, seguintes };
}
const noPeriodo = (data: string | null, p: Periodo, hoje: string) => {
  if (p === 'todo') return true;
  if (!data) return false;
  if (p.startsWith('mes:')) return data.slice(0, 7) === p.slice(4);
  if (p === 'atrasados') return data < hoje;
  const limite = new Date(Date.parse(`${hoje}T12:00:00Z`) + 30 * 86_400_000).toISOString().slice(0, 10);
  return data >= hoje && data <= limite;
};
const rotuloPeriodo = (p: Periodo) => (p === 'todo' ? 'todo o período' : p.startsWith('mes:') ? mesCurto(p.slice(4)) : p === 'atrasados' ? 'previsão vencida' : 'próximos 30 dias');
/** Códigos e números de documento inteiros: a linha pode quebrar entre eles, nunca dentro de "OCF-000022" (texto original preservado para busca e cópia). */
export const cod = (t: string | null | undefined): ReactNode => {
  const x = t ?? '';
  if (!x.includes('-')) return x;
  return x.split(/(\s+)/).map((parte, k) => (parte.includes('-') ? <span key={k} className="rc-cod">{parte}</span> : parte));
};
const abrir = (numero: string) => navegar(numero.startsWith('RAV-') ? `/contas-receber/avulsos/${numero}` : `/contas-receber/${numero}`);
const link = (numero: string) => (
  <a className="rc-codigo" href={`#/contas-receber/${numero.startsWith('RAV-') ? `avulsos/${numero}` : numero}`} onClick={(e) => (e.preventDefault(), abrir(numero))}>
    {cod(numero)}
  </a>
);
const ler = (k: string) => {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
};
const gravar = (k: string, v: string) => {
  try {
    sessionStorage.setItem(k, v);
  } catch {
    /* sem armazenamento da sessão: a escolha só não sobrevive à navegação */
  }
};
type ParcelaLinha = LinhaComissao['parcelas'][number];
type Cartao = 'previsto' | 'disponivel' | 'solicitado' | 'recebido';
const cent = (v: string | null) => (v === null ? 0 : Math.round(Number(v) * 100));
const reais = (c: number) => (c / 100).toFixed(2);

export function TelaContasReceber() {
  const [aba, setAba] = useState<'todos' | 'comissoes'>(() => (ler(CHAVE_ABA) === 'comissoes' ? 'comissoes' : 'todos'));
  const trocar = (a: 'todos' | 'comissoes') => (setAba(a), gravar(CHAVE_ABA, a));
  const carga = useCarga(() => api.get<Painel>('/api/receber/painel'), []);
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>('todo');
  const [situacao, setSituacao] = useState('');
  const [parceiro, setParceiroEstado] = useState(() => ler(CHAVE_PARCEIRO) ?? '');
  const setParceiro = (p: string) => (setParceiroEstado(p), gravar(CHAVE_PARCEIRO, p));
  const [modal, setModal] = useState<'avulso' | 'nota' | null>(null);
  const [composicao, setComposicao] = useState<Cartao | null>(null);
  const d = carga.dados;
  const hoje = d?.hoje ?? '';
  const termo = busca.trim().toLowerCase();
  const porNumero = useMemo(() => new Map((d?.comissoes.linhas ?? []).map((k) => [k.numero, k])), [d]);
  // parcelas de cada linha: a comissão tem as suas; o avulso é uma parcela só (saldo e previsão)
  const parcelasDe = (x: LinhaTodos | LinhaComissao): ParcelaLinha[] =>
    'parcelas' in x ? x.parcelas : x.tipo === 'comissao' ? (porNumero.get(x.numero)?.parcelas ?? []) : [{ item: x.numero, rotulo: '1/1', valor: x.valor, quitado: '0', recebidoCaixa: '0', aReceber: x.aReceber, data: x.previsao, disponivel: '0.00', solicitado: '0.00', resgate: null, notas: [] }];
  const parceiroDe = (x: LinhaTodos | LinhaComissao) => ('parceiroId' in x ? x.parceiroId : x.tipo === 'comissao' ? (porNumero.get(x.numero)?.parceiroId ?? null) : null);
  const filtroParceiro = aba === 'comissoes' ? parceiro : '';
  // busca e parceiro valem para os quatro cartões; período e situação só para a lista (e o Previsto, que é a soma dela)
  const passaBase = (x: LinhaTodos | LinhaComissao) => (!termo || x.busca.toLowerCase().includes(termo)) && (!filtroParceiro || parceiroDe(x) === filtroParceiro);
  const noRecorte = (p: ParcelaLinha) => noPeriodo(p.data, periodo, hoje);
  /** Previsto da linha no período: em aberto hoje, pelas parcelas com data esperada no recorte (desconhecido = nulo). */
  const previstoDe = (x: LinhaTodos | LinhaComissao): string | null => {
    if (periodo === 'todo') return x.aReceber;
    const ps = parcelasDe(x).filter(noRecorte);
    return ps.some((p) => p.aReceber === null) ? null : reais(ps.reduce((s, p) => s + cent(p.aReceber), 0));
  };
  const visivel = (x: LinhaTodos | LinhaComissao) =>
    passaBase(x) && (!situacao || x.situacao.codigo === situacao) && (periodo === 'todo' || parcelasDe(x).some((p) => noRecorte(p) && (p.aReceber === null || cent(p.aReceber) > 0)));
  const linhasBase: (LinhaTodos | LinhaComissao)[] = d ? (aba === 'todos' ? d.todos.linhas : d.comissoes.linhas) : [];
  const visiveis = linhasBase.filter(visivel);
  const todos = aba === 'todos' ? (visiveis as LinhaTodos[]) : [];
  const comissoes = aba === 'comissoes' ? (visiveis as LinhaComissao[]) : [];
  const comissoesBase = (d?.comissoes.linhas ?? []).filter((k) => passaBase(k));
  const previstos = visiveis.map(previstoDe);
  const disponivel = comissoesBase.reduce((s, k) => s + cent(k.disponivelResgate), 0);
  const solicitadoDesconhecido = comissoesBase.some((k) => k.solicitado === null);
  const solicitado = comissoesBase.reduce((s, k) => s + cent(k.solicitado), 0);
  const emVerificacao = comissoesBase.filter((k) => k.emVerificacao);
  // Recebido: dinheiro pela data efetiva; período relativo (próximos 30 dias, previsão vencida) não se aplica a ele
  const periodoRecebido = periodo === 'todo' || periodo.startsWith('mes:') ? periodo : 'todo';
  const buscaDe = new Map(linhasBase.map((x) => [x.numero, x.busca.toLowerCase()]));
  const recebidos = (d?.recebidos ?? []).filter(
    (r) =>
      !r.estornado &&
      (aba === 'todos' || (r.origem?.tipo === 'comissao' && r.origem.comissaoId)) &&
      (!filtroParceiro || r.origem?.parceiroId === filtroParceiro) &&
      noPeriodo(r.data, periodoRecebido, hoje) &&
      (!termo || [r.recebimento, r.nota ?? '', r.origem?.numero ?? '', buscaDe.get(r.origem?.numero ?? '') ?? ''].join(' ').toLowerCase().includes(termo)),
  );
  const recebido = recebidos.reduce((s, r) => s + cent(r.dinheiro), 0);
  const situacoes = d ? [...new Set(linhasBase.map((x) => x.situacao.codigo))] : [];
  const p = d?.permissoes;
  const meses = d ? mesesDoFiltro(d.inicioHistorico, hoje) : null;
  const mesPassado = periodo.startsWith('mes:') && meses !== null && periodo.slice(4) < meses.atual;
  const abas = <Abas rotulo="Contas a Receber" atual={aba} aoTrocar={trocar} abas={[{ id: 'todos', rotulo: 'Todos os recebíveis' }, { id: 'comissoes', rotulo: 'Comissões' }]} />;
  const verComposicao = (c: Cartao) => (
    <button type="button" className="botao-link rc-ver-composicao" onClick={() => setComposicao(c)} data-teste={`composicao-${c}`}>
      ver composição
    </button>
  );
  const recorte = (t: string) => (
    <span className="rc-recorte" data-teste="recorte">
      {t}
    </span>
  );
  const semValor = previstos.filter((x) => x === null).length;
  return (
    <>
      <div className="rc-cab-lista rc-cab-quatro">
        <h1 className="rc-titulo-pagina">Contas a Receber</h1>
        {d && (
          <CardsLista n={4} teste="cards-receber">
            <CardLista
              icone="documento"
              tom="roxo"
              rotulo="Previsto a receber"
              valor={totalConhecido(previstos)}
              detalhe={
                <>
                  {recorte(`em aberto hoje · ${periodo === 'todo' ? 'todo o período' : `esperado: ${rotuloPeriodo(periodo)}`}`)}
                  {semValor > 0 && <span className="rc-sec">+ {plural(semValor, 'item', 'itens')} sem estimativa (valor desconhecido, não zero)</span>}
                  {emVerificacao.length > 0 && <span className="rc-sec" data-teste="em-verificacao">+ {rs(reais(emVerificacao.reduce((s, k) => s + cent(k.emVerificacao!.valor), 0)))} em verificação (fora)</span>}
                  {verComposicao('previsto')}
                </>
              }
              ajuda={<Ajuda titulo="Previsto a receber">Valor confirmado (ou, sem confirmação, o estimado) ainda não quitado, das parcelas com data esperada no período escolhido, em aberto hoje. É a soma da coluna "Previsto" da lista. Inclui o Disponível e o Resgate solicitado (não some os cartões). Valor desconhecido não entra como zero. Comissão de Pedido cancelado ou de origem desfeita, ainda não cancelada, fica "em verificação", fora do total.</Ajuda>}
              teste="card-previsto"
            />
            <CardLista
              icone="moedas"
              tom="verde"
              rotulo="Disponível para resgate"
              valor={rs(reais(disponivel))}
              detalhe={
                <>
                  {recorte(`posição de hoje (${dataBr(hoje)})${periodo !== 'todo' ? ' · o período não se aplica' : ''}`)}
                  {verComposicao('disponivel')}
                </>
              }
              ajuda={<Ajuda titulo="Disponível para resgate">Parcelas com a liberação cumprida, valor confirmado e SEM resgate solicitado, pelo saldo ainda não cobrado em nota da Inttrix. É sempre a posição de hoje: o período escolhido não muda este cartão.</Ajuda>}
              teste="card-disponivel"
            />
            <CardLista
              icone="relogio"
              tom="azul"
              rotulo="Resgate solicitado"
              valor={solicitadoDesconhecido && solicitado === 0 ? 'A confirmar' : rs(reais(solicitado))}
              detalhe={
                <>
                  {recorte(`posição de hoje (${dataBr(hoje)})${periodo !== 'todo' ? ' · o período não se aplica' : ''}`)}
                  {verComposicao('solicitado')}
                </>
              }
              ajuda={<Ajuda titulo="Resgate solicitado">Saldo ainda não quitado das parcelas com resgate solicitado ativo: solicitado − (dinheiro + IR retido + antecipação + crédito aplicado). Recebimento parcial reduz o saldo; resgate cancelado devolve a parcela ao Disponível. Recebimento sem resgate solicitado também é aceito. Sempre a posição de hoje.</Ajuda>}
              teste="card-solicitado"
            />
            <CardLista
              icone="dinheiro"
              tom="verde"
              rotulo="Recebido"
              valor={rs(reais(recebido))}
              detalhe={
                <>
                  {recorte(`dinheiro · ${periodoRecebido === 'todo' ? `todo o período${periodo !== 'todo' ? ' (o período escolhido não se aplica)' : ''}` : `recebido em ${rotuloPeriodo(periodo)}`}${aba === 'comissoes' ? ' · comissões' : ''}`)}
                  {verComposicao('recebido')}
                </>
              }
              ajuda={<Ajuda titulo="Recebido">Dinheiro que entrou (recebimentos não estornados), pela data efetiva. IR retido, antecipação e crédito aplicado quitam a nota sem ser dinheiro e ficam à parte, na composição. Cancelamento comercial não retira o que foi recebido; só o estorno de um registro errado retira. Aguarda o extrato: a conciliação só vincula.</Ajuda>}
              teste="card-recebido"
            />
          </CardsLista>
        )}
      </div>
      {mesPassado && (
        <div className="aviso aviso-atencao-fin" data-teste="aviso-mes-passado">
          <strong>{mesExtenso(periodo.slice(4))} é um mês passado.</strong> “Disponível para resgate” e “Resgate solicitado” mostram a posição de hoje, não a do fim daquele mês: a posição histórica não é reconstruída. “Previsto” é o que continua em aberto hoje com data esperada naquele mês.
        </div>
      )}
      <div className="linha-abas-acao">
        {abas}
        <div className="acoes-direita">
          <button type="button" className="botao-link" onClick={() => navegar('/contas-receber/notas')} data-teste="ir-notas">
            Notas da Inttrix
          </button>
          <button type="button" className="botao-link" onClick={() => navegar('/contas-receber/creditos')}>
            Créditos do pagador
          </button>
          {aba === 'comissoes' && p?.completarLegado && (
            <button type="button" className="botao-link" onClick={() => navegar('/contas-receber/legado')} data-teste="ir-legado">
              Completar operações antigas
            </button>
          )}
          {aba === 'comissoes' && p?.emitirNota && (
            <button type="button" className="botao-secundario" disabled={!d || d.comissoes.linhas.reduce((s, k) => s + cent(k.disponivel), 0) <= 0} onClick={() => setModal('nota')} data-teste="acao-emitir-nota">
              Emitir nota da Inttrix
            </button>
          )}
          {aba === 'todos' && p?.registrarAvulso && (
            <button type="button" className="botao-primario" onClick={() => setModal('avulso')} data-teste="acao-novo-avulso">
              + Recebível avulso
            </button>
          )}
        </div>
      </div>
      <div className="rc-filtros">
        <CampoBusca valor={busca} setValor={setBusca} rotulo={aba === 'todos' ? 'Buscar operação, pagador ou documento' : 'Buscar comissão'} ajuda={aba === 'todos' ? 'CMS-, RAV-, pagador, origem ou documento' : 'Comissão, cliente, parceiro, pedido Inttrix, ordem, NF ou pedido interno do parceiro'} teste="busca-receber" total={termo ? visiveis.length : null} />
        {aba === 'comissoes' && (
          <label className="campo campo-compacto">
            Parceiro
            <select value={parceiro} onChange={(e) => setParceiro(e.target.value)} data-teste="filtro-parceiro">
              <option value="">Todos</option>
              {(d?.parceiros ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nome}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="campo campo-compacto">
          Período
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} data-teste="filtro-periodo">
            <option value="todo">Todo o período</option>
            {meses && <option value={`mes:${meses.atual}`}>{mesExtenso(meses.atual)} (este mês)</option>}
            {meses && meses.anteriores.length > 0 && (
              <optgroup label="Meses anteriores">
                {meses.anteriores.map((m) => (
                  <option key={m} value={`mes:${m}`}>
                    {mesExtenso(m)}
                  </option>
                ))}
              </optgroup>
            )}
            {meses && (
              <optgroup label="Próximos meses">
                {meses.seguintes.map((m) => (
                  <option key={m} value={`mes:${m}`}>
                    {mesExtenso(m)}
                  </option>
                ))}
              </optgroup>
            )}
            <option value="proximos30">Próximos 30 dias</option>
            <option value="atrasados">Previsão vencida</option>
          </select>
        </label>
        <label className="campo campo-compacto">
          Situação
          <select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-teste="filtro-situacao">
            <option value="">Todas</option>
            {situacoes.map((s) => (
              <option key={s} value={s}>
                {ROTULO_SITUACAO[s] ?? s}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="receber" />
      ) : aba === 'todos' ? (
        <TabelaTodos linhas={todos} previsto={previstoDe} vazio={d.todos.linhas.length ? 'Nenhum recebível com os filtros atuais.' : 'Nenhum recebível ainda. As comissões aparecem sozinhas quando o parceiro fatura (nota, remessa, OL ou OTI); avulsos pelo botão “Recebível avulso”.'} />
      ) : (
        <TabelaComissoes linhas={comissoes} previsto={previstoDe} vazio={d.comissoes.linhas.length ? 'Nenhuma comissão com os filtros atuais.' : 'Nenhuma comissão ainda: elas nascem sozinhas com a nota do parceiro, a remessa da OL, a OL de projeto ou a OTI.'} />
      )}
      {d && <NaoFaturado d={d} />}
      <p className="sem-margem rc-rec-linha" data-teste="reconhecimento">
        Reconhecimento da receita: {selo(RECONHECIMENTO, 'ni')}
        <Ajuda titulo="Reconhecimento">{AJUDA_RECONHECIMENTO}</Ajuda>
      </p>
      {modal === 'avulso' && <NovoAvulso aoFechar={() => setModal(null)} aoSalvar={(n) => navegar(`/contas-receber/avulsos/${n}`)} />}
      {modal === 'nota' && <EmitirNota aoFechar={() => setModal(null)} aoSalvar={(n) => navegar(`/contas-receber/notas/${n}`)} />}
      {composicao && d && (
        <ComposicaoCartao
          cartao={composicao}
          aoFechar={() => setComposicao(null)}
          hoje={hoje}
          recorte={
            composicao === 'previsto'
              ? `em aberto hoje · ${periodo === 'todo' ? 'todo o período' : `esperado: ${rotuloPeriodo(periodo)}`}`
              : composicao === 'recebido'
                ? `dinheiro pela data efetiva · ${periodoRecebido === 'todo' ? 'todo o período' : rotuloPeriodo(periodo)}`
                : `posição de hoje (${dataBr(hoje)}) · o período escolhido não se aplica`
          }
          filtros={[filtroParceiro ? `Parceiro: ${d.parceiros.find((x) => x.id === filtroParceiro)?.nome ?? ''}` : aba === 'comissoes' ? 'todos os parceiros' : 'todos os pagadores', termo ? `busca “${busca.trim()}”` : '', composicao === 'previsto' && situacao ? `situação: ${ROTULO_SITUACAO[situacao] ?? situacao}` : ''].filter(Boolean).join(' · ')}
          linhas={composicao === 'previsto' ? visiveis.map((x) => ({ numero: x.numero, parcelas: parcelasDe(x).filter((pp) => periodo === 'todo' || noRecorte(pp)) })) : comissoesBase.map((k) => ({ numero: k.numero, parcelas: k.parcelas }))}
          emVerificacao={composicao === 'previsto' ? emVerificacao.map((k) => ({ numero: k.numero, valor: k.emVerificacao!.valor, motivo: k.emVerificacao!.motivo })) : []}
          recebidos={recebidos}
          estornados={(d.recebidos ?? []).filter((r) => r.estornado && (aba === 'todos' || r.origem?.tipo === 'comissao') && (!filtroParceiro || r.origem?.parceiroId === filtroParceiro) && noPeriodo(r.data, periodoRecebido, hoje))}
        />
      )}
    </>
  );
}

/** Rótulos padronizados da situação (nenhum código cru no filtro). */
const ROTULO_SITUACAO: Record<string, string> = {
  disponivel: 'Com parcela disponível',
  resgate_solicitado: 'Resgate solicitado',
  aguardando: 'Aguardando',
  confirmar: 'A confirmar valor',
  a_confirmar: 'Liberação a confirmar',
  em_nota: 'Em nota',
  nota_cancelada: 'Nota cancelada · aguardando providência',
  quitada: 'Quitada',
  recebida: 'Recebida',
  recebido: 'Recebido',
  a_receber: 'A receber',
  parcial: 'Recebido parcialmente',
  cancelada: 'Cancelada',
  cancelado: 'Cancelado',
  verificar: 'Verificar origem',
};

/** E0: composição de cada cartão, com o recorte escrito e o acesso à origem de cada valor. */
function ComposicaoCartao({
  cartao,
  aoFechar,
  hoje,
  recorte,
  filtros,
  linhas,
  emVerificacao,
  recebidos,
  estornados,
}: {
  cartao: Cartao;
  aoFechar: () => void;
  hoje: string;
  recorte: string;
  filtros: string;
  linhas: { numero: string; parcelas: ParcelaLinha[] }[];
  emVerificacao: { numero: string; valor: string | null; motivo: string }[];
  recebidos: Painel['recebidos'];
  estornados: Painel['recebidos'];
}) {
  const titulo = { previsto: 'Previsto a receber', disponivel: 'Disponível para resgate', solicitado: 'Resgate solicitado', recebido: 'Recebido' }[cartao];
  const meta = (
    <p className="rc-meta-composicao" data-teste="recorte-composicao">
      <strong>Recorte:</strong> {recorte} · {filtros}
    </p>
  );
  let corpo: ReactNode;
  if (cartao === 'recebido') {
    const soma = (k: 'dinheiro' | 'ir' | 'antecipacao' | 'credito') => rs(reais(recebidos.reduce((s, r) => s + cent(r[k]), 0)));
    corpo = (
      <>
        <RolagemIndicada className="rolagem quadro-tabela" teste="composicao-recebido">
          <table className="tabela tabela-compacta rc-fixo">
            <thead>
              <tr>
                <th style={{ width: '14%' }}>Registro</th>
                <th style={{ width: '11%' }}>Data</th>
                <th style={{ width: '25%' }}>Origem</th>
                <th className="numero" style={{ width: '13%' }}>Dinheiro</th>
                <th className="numero" style={{ width: '12%' }}>IR retido</th>
                <th className="numero" style={{ width: '12%' }}>Antecipação</th>
                <th className="numero" style={{ width: '13%' }}>Crédito aplicado</th>
              </tr>
            </thead>
            <tbody>
              {recebidos.map((r, i) => (
                <tr key={`${r.recebimento}-${i}`} data-teste="linha-recebido">
                  <td>
                    {cod(r.recebimento)}
                    {r.nota && <span className="rc-sec">{cod(r.nota)}</span>}
                  </td>
                  <td>{dataBr(r.data)}</td>
                  <td>
                    {r.excedente !== '0.00' ? 'excedente → crédito do pagador' : r.origem ? link(r.origem.numero) : '—'}
                    {r.origemSituacao === 'cancelada' && <span className="rc-sec rc-alerta-txt">origem cancelada · recebido mantido</span>}
                    {r.origemSituacao === 'em_verificacao' && <span className="rc-sec rc-alerta-txt">origem em verificação</span>}
                  </td>
                  <td className="numero">{rs(r.dinheiro)}</td>
                  <td className="numero">{r.ir === '0.00' ? '—' : rs(r.ir)}</td>
                  <td className="numero">{r.antecipacao === '0.00' ? '—' : rs(r.antecipacao)}</td>
                  <td className="numero">{r.credito === '0.00' ? '—' : rs(r.credito)}</td>
                </tr>
              ))}
              {estornados.map((r, i) => (
                <tr key={`e-${r.recebimento}-${i}`} className="rc-estornado" data-teste="linha-estornado">
                  <td>{cod(r.recebimento)}</td>
                  <td>{dataBr(r.data)}</td>
                  <td>estornado: fora do Recebido</td>
                  <td className="numero">
                    <s>{rs(r.dinheiro)}</s>
                  </td>
                  <td colSpan={3} />
                </tr>
              ))}
              <tr className="rc-total">
                <td colSpan={3}>Total (o cartão é o dinheiro)</td>
                <td className="numero">{soma('dinheiro')}</td>
                <td className="numero">{soma('ir')}</td>
                <td className="numero">{soma('antecipacao')}</td>
                <td className="numero">{soma('credito')}</td>
              </tr>
            </tbody>
          </table>
        </RolagemIndicada>
        <p className="rc-sec">IR retido, antecipação e crédito aplicado quitam a nota sem ser dinheiro: aparecem aqui e não somam no cartão. Crédito do pagador e devolução seguem os seus fluxos; o dinheiro recebido continua aqui mesmo com a origem cancelada.</p>
      </>
    );
  } else {
    const valorDe = (pp: ParcelaLinha) => (cartao === 'previsto' ? pp.aReceber : cartao === 'disponivel' ? pp.disponivel : pp.solicitado);
    const itens = linhas.flatMap((l) => l.parcelas.filter((pp) => valorDe(pp) === null || cent(valorDe(pp)) > 0).map((pp) => ({ numero: l.numero, pp })));
    const total = itens.reduce((s, x) => s + cent(valorDe(x.pp)), 0);
    corpo = (
      <>
        <RolagemIndicada className="rolagem quadro-tabela" teste={`composicao-${cartao}`}>
          <table className="tabela tabela-compacta rc-fixo">
            <thead>
              <tr>
                <th style={{ width: '18%' }}>Origem</th>
                <th style={{ width: '10%' }}>Parcela</th>
                <th style={{ width: '18%' }}>Data esperada</th>
                <th style={{ width: '24%' }}>{cartao === 'solicitado' ? 'Resgate · nota' : 'Situação'}</th>
                <th className="numero" style={{ width: '15%' }}>{cartao === 'solicitado' ? 'Quitado' : 'Valor'}</th>
                <th className="numero" style={{ width: '15%' }}>{cartao === 'previsto' ? 'Em aberto' : cartao === 'disponivel' ? 'Disponível' : 'Saldo'}</th>
              </tr>
            </thead>
            <tbody>
              {itens.map(({ numero, pp }) => (
                <tr key={`${numero}-${pp.item}`} data-teste={`linha-composicao-${cartao}`}>
                  <td>{link(numero)}</td>
                  <td>{pp.rotulo}</td>
                  <td>
                    {pp.data ? dataBr(pp.data) : 'sem data'}
                    {pp.data && pp.data < hoje && cartao === 'previsto' && <span className="rc-sec rc-alerta-txt">vencida</span>}
                  </td>
                  <td>
                    {cartao === 'solicitado' ? (
                      <>
                        {pp.resgate}
                        {pp.notas.length > 0 && <span className="rc-sec">{pp.notas.join(', ')}</span>}
                      </>
                    ) : pp.resgate ? (
                      `resgate ${pp.resgate}`
                    ) : pp.notas.length ? (
                      `em nota ${pp.notas.join(', ')}`
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="numero">{cartao === 'solicitado' ? rs(pp.quitado) : valorOuConfirmar(pp.valor)}</td>
                  <td className="numero">{valorOuConfirmar(valorDe(pp))}</td>
                </tr>
              ))}
              <tr className="rc-total">
                <td colSpan={5}>Total = cartão</td>
                <td className="numero">{rs(reais(total))}</td>
              </tr>
            </tbody>
          </table>
        </RolagemIndicada>
        {cartao === 'previsto' && emVerificacao.length > 0 && (
          <div className="aviso" data-teste="composicao-em-verificacao">
            <strong>Em verificação (fora do Previsto):</strong>{' '}
            {emVerificacao.map((x) => `${x.numero} ${valorOuConfirmar(x.valor)} (${x.motivo})`).join('; ')}. O saldo não foi extinto por ninguém: decida na ficha (cancelar a comissão ou mantê-la).
          </div>
        )}
        {cartao === 'disponivel' && <p className="rc-sec">Posição de hoje: pode incluir parcela com data esperada fora do período da lista.</p>}
        {cartao === 'solicitado' && <p className="rc-sec">Saldo = solicitado − quitado (dinheiro + IR retido + antecipação + crédito aplicado). Recebimento parcial reduz o saldo.</p>}
      </>
    );
  }
  return (
    <Modal titulo={`${titulo} — composição`} aoFechar={aoFechar} largo rodape={<button type="button" className="botao-secundario" onClick={aoFechar}>Fechar</button>}>
      {meta}
      {corpo}
    </Modal>
  );
}

/** Total só do que é conhecido; sem nenhum valor conhecido, "A confirmar" (desconhecido não é zero). */
function totalConhecido(xs: (string | null)[]) {
  if (xs.length && xs.every((x) => x === null)) return 'A confirmar';
  return rs(xs.reduce((s, x) => s + (x === null ? 0 : Number(x)), 0));
}

/** Causa curta da previsão vencida (o texto completo fica na dica): nunca presume atraso do parceiro. */
const vencidaCurta = (t: string) =>
  /prazo negociado/.test(t) ? 'prazo negociado vencido' : /reconhecimento/.test(t) ? 'aguardando reconhecimento' : /Cliente/.test(t) ? 'aguardando Cliente' : /liberação/.test(t) ? 'aguardando liberação' : /resgate/.test(t) ? 'aguardando resgate' : /nota/.test(t) ? 'aguardando nota' : /repasse/.test(t) ? 'repasse vencido' : t;
/** Data esperada de recebimento: estimada × efetiva; o que falta; previsão vencida com a causa. */
function DataEsperada({ p }: { p: ProximaPrevisao | null }) {
  if (!p) return <span className="texto-secundario">—</span>;
  if (!p.data)
    return (
      <span className="rc-falta" data-teste="data-esperada-falta">
        {selo('Sem previsão', 'pend')}
        <span className="rc-sec">falta: {p.falta.join('; ') || 'condições da operação'}</span>
      </span>
    );
  return (
    <span data-teste="data-esperada">
      <span className="rc-cod">{dataBr(p.data)}</span>
      <span className="rc-sec">
        {p.origem === 'negociada' ? 'prazo negociado' : p.estimada ? 'estimada' : 'efetiva'}
        {p.abertas > 1 ? ` · próxima de ${p.abertas}` : ''}
      </span>
      {p.origem === 'negociada' && p.calculada !== p.data && <span className="rc-sec">cálculo: {p.calculada ? dataBr(p.calculada) : 'sem dados'}</span>}
      {p.vencida && (
        <span className="rc-sec rc-alerta-txt" title={p.vencida}>
          vencida · {vencidaCurta(p.vencida)}
        </span>
      )}
    </span>
  );
}

function TabelaTodos({ linhas, previsto, vazio }: { linhas: LinhaTodos[]; previsto: (x: LinhaTodos) => string | null; vazio: string }) {
  if (!linhas.length)
    return (
      <p className="vazio" data-teste="receber-vazia">
        {vazio}
      </p>
    );
  const valores = linhas.map(previsto);
  return (
    <RolagemIndicada className="rolagem quadro-tabela" teste="receber">
      <table className="tabela tabela-compacta rc-fixo" data-teste="lista-todos">
        <thead>
          <tr>
            <th style={{ width: '14%' }}>Operação</th>
            <th style={{ width: '18%' }}>Pagador</th>
            <th style={{ width: '24%' }}>Origem</th>
            <th className="numero" style={{ width: '14%' }}>
              Valor
            </th>
            <th style={{ width: '15%' }}>
              Receb. esperado
              <Ajuda titulo="Recebimento esperado">Data esperada de recebimento pela Inttrix da próxima parcela em aberto: estimada (pelas condições e pelo vencimento do Cliente) até o fato ocorrer. Sem dado suficiente, mostra o que falta. O calendário completo fica na ficha.</Ajuda>
            </th>
            <th style={{ width: '15%' }}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.numero} data-teste={`linha-${l.numero}`}>
              <td>
                {link(l.numero)}
                <span className="rc-sec">{l.tipoRotulo}</span>
              </td>
              <td>{l.pagador}</td>
              <td className="texto-secundario">{cod(l.origem)}</td>
              <td className="numero rc-num">
                {valorOuConfirmar(previsto(l))}
                {previsto(l) !== null && l.valor !== null && previsto(l) !== l.valor && <span className="rc-sec">de {rs(l.valor)}</span>}
              </td>
              <td>
                <DataEsperada p={l.proximaPrevisao} />
              </td>
              <td>
                <Situacao rotulo={l.situacao.rotulo} tom={l.situacao.tom} />
              </td>
            </tr>
          ))}
          <tr className="rc-total">
            <td colSpan={3}>Soma da coluna (= cartão “Previsto a receber”)</td>
            <td className="numero rc-num" data-teste="soma-previsto">{totalConhecido(valores)}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </RolagemIndicada>
  );
}

function TabelaComissoes({ linhas, previsto, vazio }: { linhas: LinhaComissao[]; previsto: (x: LinhaComissao) => string | null; vazio: string }) {
  if (!linhas.length)
    return (
      <p className="vazio" data-teste="receber-vazia">
        {vazio}
      </p>
    );
  return (
    <RolagemIndicada className="rolagem quadro-tabela" teste="receber">
      <table className="tabela tabela-compacta rc-fixo" data-teste="lista-comissoes">
        <thead>
          <tr>
            <th style={{ width: '13%' }}>Comissão</th>
            <th style={{ width: '17%' }}>Cliente / Parceiro</th>
            <th style={{ width: '12%' }}>Pedido / Ordem</th>
            <th style={{ width: '13%' }}>NF do parceiro</th>
            <th className="rc-direita" style={{ width: '12%' }}>
              Previsto
            </th>
            <th style={{ width: '14%' }}>
              Receb. esperado
              <Ajuda titulo="Recebimento esperado">Próxima parcela em aberto: data esperada de recebimento pela Inttrix (estimada até o fato ocorrer) ou o que falta para prever. Previsão vencida mostra a causa provável (Cliente, liberação, resgate ou repasse), nunca presume atraso do parceiro.</Ajuda>
            </th>
            <th style={{ width: '19%' }}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.numero} data-teste={`linha-${l.numero}`}>
              <td>
                {link(l.numero)}
                <span className="rc-sec">{l.modalidadeRotulo}</span>
              </td>
              <td>
                {l.cliente}
                <span className="rc-sec">{l.parceiro}</span>
              </td>
              <td>
                {cod(l.pedido)}
                <span className="rc-sec">{cod(l.ocf ?? l.documento ?? '—')}</span>
              </td>
              <td>
                {l.nf ? cod(l.nf) : '—'}
                {l.nfData && <span className="rc-sec">{dataBr(l.nfData)}</span>}
                {l.pedidoInterno && <span className="rc-sec">ped. interno {cod(l.pedidoInterno)}</span>}
              </td>
              <td className="rc-direita">
                <span className="rc-valor">{valorOuConfirmar(previsto(l))}</span>
                {Number(l.disponivelResgate) > 0 && <span className="rc-sec">disponível {rs(l.disponivelResgate)}</span>}
                {l.solicitado !== null && Number(l.solicitado) > 0 && <span className="rc-sec">solicitado {rs(l.solicitado)}</span>}
                {l.emVerificacao && <span className="rc-sec rc-alerta-txt">em verificação {valorOuConfirmar(l.emVerificacao.valor)}</span>}
                <Pendencia motivo={l.pendenciaEstimativa} />
              </td>
              <td>
                <DataEsperada p={l.proximaPrevisao} />
              </td>
              <td>
                <Situacao rotulo={l.situacao.rotulo} tom={l.situacao.tom} />
                {l.alerta && <span className="rc-sec rc-alerta-txt">{l.alerta}</span>}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="rc-total">
            <td colSpan={4}>Soma da coluna (= cartão “Previsto a receber”)</td>
            <td className="rc-direita" data-teste="soma-previsto">
              {totalConhecido(linhas.map(previsto))}
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </RolagemIndicada>
  );
}

function NaoFaturado({ d }: { d: Painel }) {
  if (!d.naoFaturado.linhas.length) return null;
  return (
    <details className="rc-recolhido" data-teste="nao-faturado">
      <summary>
        Ainda não faturado · previsões e backlog <span className="texto-secundario">({plural(d.naoFaturado.linhas.length, 'operação', 'operações')} · {d.naoFaturado.total === null ? 'estimativa a confirmar' : `${rs(d.naoFaturado.total)} estimado${d.naoFaturado.semValor ? ` + ${d.naoFaturado.semValor} a confirmar` : ''}`}, fora dos totais)</span>
      </summary>
      <div>
        <p className="texto-secundario sem-margem">
          Previsão de comissão de pedidos ainda não faturados pelo parceiro: não é comissão originada, não tem vencimento e não entra no previsto a receber. A comissão nasce sozinha com a nota do parceiro, a remessa da OL, a OL de projeto ou a OTI.
        </p>
        <RolagemIndicada className="rolagem quadro-tabela" teste="tabela-nao-faturado">
          <table className="tabela tabela-compacta rc-fixo">
            <thead>
              <tr>
                <th style={{ width: '16%' }}>Pedido</th>
                <th style={{ width: '14%' }}>Modalidade</th>
                <th style={{ width: '26%' }}>Cliente / Parceiro</th>
                <th className="rc-direita" style={{ width: '14%' }}>
                  Estimativa
                </th>
                <th style={{ width: '30%' }}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {d.naoFaturado.linhas.map((x) => (
                <tr key={`${x.pedido}-${x.parceiro}`}>
                  <td>
                    {cod(x.pedido)}
                    {(x.ocfs.length > 0 || x.documento) && <span className="rc-sec">{cod([...x.ocfs, x.documento].filter(Boolean).join(' · '))}</span>}
                  </td>
                  <td>{MODALIDADES[x.modalidade]}</td>
                  <td>
                    {x.cliente}
                    <span className="rc-sec">{x.parceiro}</span>
                  </td>
                  <td className="rc-direita">
                    <span className="rc-valor">{valorOuConfirmar(x.estimativa)}</span>
                    <Pendencia motivo={x.pendenciaEstimativa} />
                  </td>
                  <td className="texto-secundario">{x.motivo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------- ficha da comissão
function textoCampo(campo: 'liberacao' | 'repasse' | 'confirmacao', c: CondicaoOperacao) {
  if (campo === 'liberacao') return textoLiberacao(c.liberacao);
  if (campo === 'repasse') return textoRepasse(c.repasse);
  return c.confirmacao.valor ? ORIGEM_CONF[c.confirmacao.valor] : 'A confirmar';
}
const origemTexto = (f: { origem: OrigemCampo | null; detalhe: string }) => (!f.origem ? 'não definido na operação' : f.origem === 'regra_parceiro' ? f.detalhe : `${ROTULO_ORIGEM[f.origem]}${f.origem === 'ajuste' || f.origem === 'definicao' ? '' : ` · ${f.detalhe}`}`);

type ModalFicha = { tipo: 'atualizar'; item: string } | { tipo: 'estorno'; id: string } | { tipo: 'condicao'; sugestao?: string } | { tipo: 'redistribuir' } | { tipo: 'calendario'; modo: ModoCalendario } | { tipo: 'previsao'; item: string } | { tipo: 'referencia' } | { tipo: 'cancelar' } | { tipo: 'nota' } | { tipo: 'resgate' } | { tipo: 'estorno_reconhecimento'; id: string } | { tipo: 'cancelar_resgate'; numero: string } | null;
export function FichaComissao({ numero }: { numero: string }) {
  const carga = useCarga(() => api.get<Ficha>(`/api/receber/comissoes/${encodeURIComponent(numero)}`), [numero]);
  const [modal, setModal] = useState<ModalFicha>(null);
  const { pode } = useSessao();
  const f = carga.dados;
  if (!f) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ficha-comissao" />;
  const fechar = () => setModal(null);
  const salvo = () => (setModal(null), void carga.recarregar());
  const p = f.permissoes;
  const trade = f.modalidade === 'trade_in';
  const ativa = !f.cancelamento;
  const soma = (xs: (string | null | undefined)[]) => xs.reduce((s, x) => s + (x === null || x === undefined ? 0 : Number(x)), 0);
  const estimado = f.itens.some((i) => i.estimado === null) ? null : soma(f.itens.map((i) => i.estimado));
  const confirmados = f.itens.filter((i) => i.confirmado);
  const lnk = (rotulo: string, rota: string | null, teste?: string) =>
    rota ? (
      <a className="rc-codigo" href={`#${rota}`} onClick={(e) => (e.preventDefault(), navegar(rota))} data-teste={teste}>
        {cod(rotulo)}
      </a>
    ) : (
      <span>{cod(rotulo)}</span>
    );
  const historico = [
    ...f.condicoes.map((c) => ({
      em: c.em,
      texto:
        c.tipo === 'abertura'
          ? `Comissão criada automaticamente (${f.origemTipo === 'nota_parceiro' ? `NF ${f.nf} do parceiro` : f.origemTipo === 'remessa' ? `remessa NF ${f.nf}` : f.documento}) · liberação ${textoLiberacao(c.condicao.liberacao)}; repasse ${textoRepasse(c.condicao.repasse)}`
          : `Condição ${c.tipo === 'definicao' ? 'definida' : 'ajustada'}: liberação ${textoLiberacao(c.condicao.liberacao)}; repasse ${textoRepasse(c.condicao.repasse)}${c.justificativa ? ` · ${c.justificativa}` : ''}`,
      por: c.por,
    })),
    ...f.distribuicoes.filter((d) => d.tipo === 'redistribuicao').map((d) => ({ em: d.em, texto: `Redistribuição das parcelas · ${d.motivo}${d.origem ? ` · ${ROTULO_ORIGEM_DISTRIBUICAO[d.origem] ?? d.origem}` : ''}${d.dataInfo ? ` (informação de ${dataBr(d.dataInfo)})` : ''}`, por: d.por })),
    ...f.distribuicoes.filter((d) => d.tipo === 'calendario').map((d) => ({ em: d.em, texto: `Calendário do Cliente informado · ${d.motivo} · ${ROTULO_ORIGEM_DISTRIBUICAO[d.origem ?? ''] ?? d.origem}${d.documento ? ` (${d.documento})` : ''}${d.editadas?.length ? ` · vencimento editado manualmente: ${d.editadas.map((e) => `${dataBr(e.sugerido)} (sugestão) → ${dataBr(e.vencimento)}`).join(', ')}` : ''}`, por: d.por })),
    ...f.pagamentos.flatMap((pg) => [
      { em: pg.registradoEm, texto: `${trade ? 'Pagamento do comprador ao cliente' : 'Pagamento do cliente final ao parceiro'}: ${rs(pg.valor)} em ${dataBr(pg.data)} (data efetiva) · ${ORIGEM_PAGAMENTO[pg.origem] ?? pg.origem}`, por: pg.por },
      ...(pg.estorno ? [{ em: pg.estorno.em, texto: `Pagamento de ${dataBr(pg.data)} estornado · ${pg.estorno.justificativa}`, por: pg.estorno.por }] : []),
    ]),
    ...f.itens.flatMap((i) => [
      ...i.confirmacoes.map((c) => ({ em: c.em, texto: `Parcela ${i.ordem}/${i.total}: valor confirmado ${rs(c.valor)} · ${ORIGEM_CONF[c.origem] ?? c.origem}${c.justificativa ? ` · correção: ${c.justificativa}` : ''}`, por: c.por })),
      ...i.previsao.replanejamentos.map((r) => ({ em: r.em, texto: `Parcela ${i.ordem}/${i.total}: previsão replanejada para ${dataBr(r.previsao)} · ${r.motivo}`, por: r.por })),
    ]),
    ...f.referencias.map((r) => ({ em: r.em, texto: r.pedidoInterno ? `Pedido interno do parceiro: ${r.pedidoInterno}` : 'Pedido interno do parceiro removido', por: r.por })),
    ...(f.cancelamento ? [{ em: f.cancelamento.em, texto: `Comissão cancelada · ${f.cancelamento.justificativa}`, por: f.cancelamento.por }] : []),
  ].sort((a, b) => b.em.localeCompare(a.em));
  const subtitulo = f.origemTipo === 'nota_parceiro' ? `Parceiro ${f.parceiro} · NF do parceiro ${f.nf}` : f.origemTipo === 'remessa' ? `Parceiro ${f.parceiro} · Remessa NF ${f.nf}` : `Parceiro ${f.parceiro} · ${f.documento}`;
  const notasAtivas = [...new Map(f.itens.flatMap((i) => i.cobranca.notas).filter((n) => n.situacao !== 'cancelada' && n.situacao !== 'substituida').map((n) => [n.numero, n])).values()];
  return (
    <>
      <div className="rc-cab">
        <div>
          <h1 className="rc-titulo" data-teste="titulo-comissao">
            {cod(f.numero)} · {f.cliente}
          </h1>
          <span className="texto-secundario">
            {subtitulo} · {MODALIDADES[f.modalidade]}
          </span>
        </div>
        {ativa && (p.ajustarCondicao || p.redistribuir || p.cancelarComissao || p.emitirNota || p.solicitarResgate) && (
          <details className="rc-menu" data-teste="menu-acoes">
            <summary className="botao-secundario">Ações ▾</summary>
            {/* escolher uma ação fecha o menu (a janela abre sem o menu por trás) */}
            <div className="rc-menu-lista" onClick={(e) => (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open')}>
              {p.emitirNota && Number(f.resumo.disponivel) > 0 && (
                <button type="button" onClick={() => setModal({ tipo: 'nota' })} data-teste="acao-nota">
                  Emitir nota da Inttrix
                </button>
              )}
              {p.solicitarResgate && f.itens.some((i) => i.liberacao.situacao === 'liberado' && !i.resgate && i.cobranca.situacao !== 'quitado') && (
                <button type="button" onClick={() => setModal({ tipo: 'resgate' })} data-teste="acao-resgate">
                  Registrar solicitação de resgate
                </button>
              )}
              {p.ajustarCondicao && (
                <button type="button" onClick={() => setModal({ tipo: 'condicao' })} data-teste="acao-condicao">
                  Condições da operação
                </button>
              )}
              {p.ajustarCondicao && f.origemTipo === 'nota_parceiro' && (
                <button type="button" onClick={() => setModal({ tipo: 'referencia' })} data-teste="acao-referencia">
                  Pedido interno do parceiro
                </button>
              )}
              {p.redistribuir && f.calendario && !f.calendario.estruturado && (f.diagnosticoCalendario.podeDividir || f.diagnosticoCalendario.podeCompletarVencimento) && (
                <button type="button" onClick={() => setModal({ tipo: 'calendario', modo: f.diagnosticoCalendario.podeDividir ? 'lista' : 'complemento' })} data-teste="acao-calendario">
                  {f.diagnosticoCalendario.podeDividir ? 'Informar calendário do Cliente' : 'Completar o vencimento da parcela'}
                </button>
              )}
              {p.redistribuir && (
                <button type="button" onClick={() => setModal({ tipo: 'redistribuir' })} data-teste="acao-redistribuir">
                  Redistribuir parcelas
                </button>
              )}
              {p.cancelarComissao && (
                <button type="button" onClick={() => setModal({ tipo: 'cancelar' })} data-teste="acao-cancelar">
                  Cancelar comissão
                </button>
              )}
            </div>
          </details>
        )}
      </div>
      {f.origemDesfeita && !f.cancelamento && (
        <div className="aviso aviso-erro" role="alert" data-teste="origem-desfeita">
          {f.origemDesfeita}. Fora do previsto a receber, sem duplicar valores; nada foi alterado automaticamente.
          {f.vinculosOrigemDesfeita.length > 0 ? (
            <> Fatos preservados: {f.vinculosOrigemDesfeita.join('; ')}. Trate-os (cancelar ou reemitir a nota, estornar registros incorretos) antes de cancelar a comissão.</>
          ) : (
            <> Se for o caso, cancele esta comissão com justificativa.</>
          )}
        </div>
      )}
      {f.alertaRefaturamento && !f.cancelamento && (
        <div className="aviso" role="alert" data-teste="alerta-refaturamento">
          {f.alertaRefaturamento}
        </div>
      )}
      {f.pedidoCancelado && <div className="aviso aviso-erro" role="alert">O Pedido desta comissão foi cancelado (ou está na Lixeira) depois da criação: nada foi alterado automaticamente.</div>}
      {f.cancelamento && (
        <div className="aviso" data-teste="comissao-cancelada">
          Comissão cancelada em {dataHora(f.cancelamento.em)}: {f.cancelamento.justificativa}
        </div>
      )}
      <div className="rc-topo">
        <div className="rc-caixa" data-teste="origem">
          <h3>Origem da comissão</h3>
          <dl className="rc-origem-bloco">
            <div>
              <dt>Pedido</dt>
              <dd>{lnk(f.pedido, pode('visualizar', 'pedido') ? `/pedidos/${f.pedido}` : null, 'link-pedido')}</dd>
            </div>
            {f.ocf && (
              <div>
                <dt>OCF</dt>
                <dd>{lnk(f.ocf, pode('visualizar', 'ordem') ? `/ordens/${f.ocf}` : null, 'link-ocf')}</dd>
              </div>
            )}
            {f.origemTipo !== 'nota_parceiro' && f.documento && (
              <div>
                <dt>{trade ? 'OTI' : 'Ordem de Locação'}</dt>
                <dd>{lnk(f.documento, pode('visualizar', 'ordem') ? `/ordens/${f.documento}` : null)}</dd>
              </div>
            )}
            {f.nf && (
              <div>
                <dt>{f.origemTipo === 'remessa' ? 'Remessa (NF)' : 'NF do parceiro'}</dt>
                <dd>
                  {f.nf} · {dataBr(f.nfData)}
                </dd>
              </div>
            )}
            {f.origemTipo === 'nota_parceiro' && (
              <div>
                <dt>Pedido interno do parceiro</dt>
                <dd data-teste="pedido-interno">
                  {f.pedidoInterno ?? <span className="texto-secundario">não informado</span>}
                  {f.pedidoInternoOrigem === 'ocf' && <span className="rc-sec">da {f.ocf}</span>}
                  {f.pedidoInternoDivergencia && (
                    <span className="rc-sec rc-alerta-txt" data-teste="pedido-interno-divergencia">
                      {f.pedidoInternoDivergencia}
                    </span>
                  )}
                </dd>
              </div>
            )}
            <div>
              <dt>Oportunidade</dt>
              <dd>{lnk(f.oportunidade, pode('visualizar', 'oportunidade') ? `/oportunidades/${f.oportunidadeId}` : null)}</dd>
            </div>
            <div>
              <dt>Proposta aceita</dt>
              <dd>{lnk(`${f.proposta} v${f.versao}`, pode('visualizar', 'proposta') ? `/oportunidades/${f.oportunidadeId}/propostas/${f.propostaId}/v/${f.versao}` : null)}</dd>
            </div>
          </dl>
          {f.irmas.length > 0 && (
            <p className="rc-sec sem-margem">
              Outras comissões desta operação:{' '}
              {f.irmas.map((x, k) => (
                <span key={x.numero}>
                  {k ? ', ' : ''}
                  {link(x.numero)}
                  {x.nf ? ` (NF ${x.nf})` : ''}
                </span>
              ))}
            </p>
          )}
        </div>
        <div>
          <div className="rc-cards3">
            <div className="rc-card">
              <b>Estimado</b>
              <strong data-teste="card-estimado">{valorOuConfirmar(estimado === null ? null : String(estimado))}</strong>
              <Pendencia motivo={f.base.pendenciaEstimativa} />
            </div>
            <div className="rc-card">
              <b>Confirmado</b>
              <strong data-teste="card-confirmado">{confirmados.length ? rs(soma(confirmados.map((i) => i.confirmado!.valor))) : '—'}</strong>
              <span className="rc-sec">{plural(confirmados.length, 'parcela', 'parcelas')} de {f.itens.length}</span>
            </div>
            <div className="rc-card verde">
              <b>Disponível para resgate</b>
              <strong data-teste="card-disponivel">{rs(f.resumo.disponivelResgate)}</strong>
              {f.resumo.solicitado !== null && Number(f.resumo.solicitado) > 0 && <span className="rc-sec" data-teste="card-solicitado-ficha">resgate solicitado {rs(f.resumo.solicitado)}</span>}
            </div>
          </div>
          <div className="rc-condicao" data-teste="condicao">
            <span>
              <span className="rc-sec">Condição de liberação da comissão</span>
              {f.condicao.liberacao.origem ? <strong>{textoLiberacao(f.condicao.liberacao)}</strong> : selo('Liberação a confirmar', 'pend')}
              {f.condicao.liberacao.origem && <span className="rc-sec">{origemTexto(f.condicao.liberacao)}</span>}
              <span className="rc-sec">
                Repasse do parceiro: {f.condicao.repasse.origem ? `${textoRepasse(f.condicao.repasse)} (${origemTexto(f.condicao.repasse)})` : 'a confirmar'}
              </span>
              {ativa &&
                p.ajustarCondicao &&
                f.sugestoes
                  .filter((x) => x.campo === 'liberacao' && !f.condicao.liberacao.origem)
                  .map((x) => (
                    <span key={x.id} className="rc-sec" data-teste={`sugestao-${x.id}`}>
                      Sugestão ({x.origem === 'sugestao_parceiro' ? 'cadastro atual do parceiro' : 'frequência da comissão'}): {x.texto} ·{' '}
                      <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'condicao', sugestao: x.id })}>
                        conferir e usar
                      </button>
                    </span>
                  ))}
            </span>
            {ativa && p.ajustarCondicao && (
              <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'condicao' })} data-teste="abrir-condicoes">
                {f.condicao.liberacao.origem ? 'Condições' : 'Definir'}
              </button>
            )}
          </div>
        </div>
      </div>
      {f.calendario && <CalendarioCliente f={f} podeInformar={Boolean(ativa && p.redistribuir)} aoInformar={(modo) => setModal({ tipo: 'calendario', modo })} />}
      <h3 className="rc-subtitulo">{trade ? 'Comissão do Trade-in' : f.modalidade === 'locacao_projeto' ? 'Comissão do projeto (única)' : f.modalidade === 'locacao_mensal' ? 'Mensalidades da comissão' : 'Parcelas da comissão'}</h3>
      <RolagemIndicada className="rolagem quadro-tabela" teste="parcelas">
        <table className="tabela tabela-compacta rc-fixo" data-teste="itens-comissao">
          <thead>
            <tr>
              <th style={{ width: '14%' }}>{f.modalidade === 'locacao_mensal' ? 'Mensalidade' : 'Parcela'}</th>
              <th style={{ width: '11%' }}>{trade ? 'Pagto. ao Cliente' : 'Venc. do cliente'}</th>
              <th style={{ width: '13%' }}>{trade ? 'Pago pelo comprador' : 'Pago pelo cliente'}</th>
              <th style={{ width: '15%' }}>
                Receb. esperado
                <Ajuda titulo="Recebimento esperado">Data esperada de recebimento pela Inttrix, desde o faturamento: vencimento do Cliente + reconhecimento do pagamento + prazo de repasse a partir do marco acordado. Estimada até o fato ocorrer; a original fica preservada. Sem dado suficiente, mostra o que falta.</Ajuda>
              </th>
              <th className="numero" style={{ width: '12%' }}>
                Comissão est.
              </th>
              <th className="numero" style={{ width: '13%' }}>
                Confirmado
              </th>
              <th style={{ width: '22%' }}>Situação · ação</th>
            </tr>
          </thead>
          <tbody>
            {f.itens.map((i) => {
              const par = f.parcelas.find((x) => x.item === i.id);
              const pagoCent = Math.round(Number(par?.pago ?? 0) * 100);
              const baseCent = i.base === null ? null : Math.round(Number(i.base) * 100);
              const s = situacaoParcela(i, pagoCent, baseCent);
              return (
                <tr key={i.id} data-teste={`item-${i.ordem}`}>
                  <td>
                    <strong>
                      {i.ordem} / {i.total}
                    </strong>
                    <span className="rc-sec">{cod(i.rotulo)}</span>
                  </td>
                  <td className="rc-num">{trade ? dataBr(i.previsao.projecao.pagamentoCliente.data) : dataBr(i.vencimentoParcela)}</td>
                  <td className="rc-num" data-teste={`pago-${i.ordem}`}>
                    {rs(par?.pago ?? 0)}
                    {i.base !== null && <span className="rc-sec">de {rs(i.base)}</span>}
                    {!i.previsao.projecao.pagamentoCliente.estimada && i.previsao.projecao.pagamentoCliente.data && <span className="rc-sec">em {dataBr(i.previsao.projecao.pagamentoCliente.data)}</span>}
                  </td>
                  <td data-teste={`previsao-${i.ordem}`}>
                    {i.previsao.atual ? (
                      <>
                        <span className="rc-cod">{dataBr(i.previsao.atual)}</span>
                        <span className="rc-sec">{i.previsao.origemAtual === 'negociada' ? 'prazo negociado' : i.previsao.projecao.recebimento.efetivo ? 'recebido' : i.previsao.estimada ? 'estimada' : 'efetiva'}</span>
                        {i.previsao.origemAtual === 'negociada' && i.previsao.calculada !== i.previsao.atual && <span className="rc-sec">cálculo {i.previsao.calculada ? dataBr(i.previsao.calculada) : 'sem dados'}</span>}
                        {i.previsao.calculoAposNegociado && <span className="rc-sec rc-alerta-txt">novo fato: {i.previsao.calculoAposNegociado.motivo}</span>}
                        {i.previsao.original && i.previsao.original !== i.previsao.atual && <span className="rc-sec">original {dataBr(i.previsao.original)}</span>}
                        {i.previsao.projecao.vencida && (
                          <span className="rc-sec rc-alerta-txt" title={i.previsao.projecao.vencida}>
                            vencida · {vencidaCurta(i.previsao.projecao.vencida)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="rc-sec" data-teste={`falta-${i.ordem}`}>falta: {i.previsao.projecao.falta.join('; ')}</span>
                    )}
                  </td>
                  <td className="numero rc-num">{valorOuConfirmar(i.estimado)}</td>
                  <td className="numero rc-num">{i.confirmado ? rs(i.confirmado.valor) : '—'}</td>
                  <td>
                    <Situacao rotulo={s.rotulo} tom={s.tom} teste={`situacao-${i.ordem}`} />
                    {ativa && (p.registrarPagamento || p.confirmarValor) && i.cobranca.situacao === 'nao_cobrado' && (
                      <button type="button" className="botao-secundario botao-pequeno rc-botao-linha" onClick={() => setModal({ tipo: 'atualizar', item: i.id })} data-teste={`atualizar-${i.ordem}`}>
                        Atualizar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RolagemIndicada>
      <CalendarioPrevisao f={f} />
      {f.origemTipo === 'nota_parceiro' && f.operacao?.backlog && Number(f.operacao.backlog) > 0 && <p className="rc-info">Outra NF da mesma OCF gera outra comissão. Saldo ainda não faturado pelo parceiro permanece no backlog ({rs(f.operacao.backlog)} na operação).</p>}
      {notasAtivas.length > 0 && (
        <p className="rc-info">
          Em nota da Inttrix:{' '}
          {notasAtivas.map((n, k) => (
            <span key={n.numero}>
              {k ? ', ' : ''}
              <a className="rc-codigo" href={`#/contas-receber/notas/${n.numero}`} onClick={(e) => (e.preventDefault(), navegar(`/contas-receber/notas/${n.numero}`))}>
                {n.numero}
              </a>{' '}
              (NF {n.numeroFiscal})
            </span>
          ))}
        </p>
      )}
      <details className="rc-recolhido" data-teste="historico">
        <summary>Histórico e documentos</summary>
        <div>
          {f.pagamentos.length > 0 && (
            <ul className="rc-hist" data-teste="pagamentos-cliente">
              {f.pagamentos.map((pg) => (
                <li key={pg.id} className={pg.estorno ? 'anulado' : undefined}>
                  <span>{dataBr(pg.data)}</span>
                  <span>
                    {f.itens.find((i) => i.parcela === pg.parcela)?.rotulo ?? pg.parcela} · {rs(pg.valor)} · {ORIGEM_PAGAMENTO[pg.origem] ?? pg.origem} · registrado em {dataHora(pg.registradoEm)}
                    {pg.por ? ` por ${pg.por}` : ''}
                    {pg.estorno ? (
                      <span className="texto-secundario"> · estornado ({pg.estorno.justificativa})</span>
                    ) : (
                      p.estornarPagamento && (
                        <>
                          {' '}
                          <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'estorno', id: pg.id })} data-teste={`estornar-${pg.id}`}>
                            Estornar
                          </button>
                        </>
                      )
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {f.reconhecimentos.length > 0 && (
            <ul className="rc-hist" data-teste="reconhecimentos">
              {f.reconhecimentos.map((r) => (
                <li key={r.id} className={r.estorno ? 'anulado' : undefined}>
                  <span>{dataBr(r.data)}</span>
                  <span>
                    Reconhecimento pelo parceiro · {f.itens.find((i) => i.parcela === r.parcela)?.rotulo ?? r.parcela} · registrado em {dataHora(r.registradoEm)}
                    {r.por ? ` por ${r.por}` : ''}
                    {r.estorno ? (
                      <span className="texto-secundario"> · estornado ({r.estorno.justificativa})</span>
                    ) : (
                      p.estornarPagamento && (
                        <>
                          {' '}
                          <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'estorno_reconhecimento', id: r.id })} data-teste={`estornar-rec-${r.id}`}>
                            Estornar
                          </button>
                        </>
                      )
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {f.resgates.length > 0 && (
            <ul className="rc-hist" data-teste="resgates">
              {f.resgates.map((g) => (
                <li key={g.numero} className={g.cancelamento ? 'anulado' : undefined}>
                  <span>{dataBr(g.data)}</span>
                  <span>
                    Solicitação de resgate {g.numero} · {g.itens.length} item(ns){g.meio ? ` · ${g.meio}` : ''}{g.por ? ` · ${g.por}` : ''}
                    {g.cancelamento ? (
                      <span className="texto-secundario"> · cancelada ({g.cancelamento.justificativa})</span>
                    ) : (
                      p.solicitarResgate && (
                        <>
                          {' '}
                          <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'cancelar_resgate', numero: g.numero })} data-teste={`cancelar-${g.numero}`}>
                            Cancelar
                          </button>
                        </>
                      )
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <ul className="rc-hist">
            {historico.map((h, n) => (
              <li key={n}>
                <span>{dataHora(h.em)}</span>
                <span>
                  {h.texto}
                  {h.por ? ` · ${h.por}` : ''}
                </span>
              </li>
            ))}
          </ul>
          {ativa && p.ajustarCondicao && (
            <p className="sem-margem">
              {f.itens.map((i) => (
                <button key={i.id} type="button" className="botao-link" onClick={() => setModal({ tipo: 'previsao', item: i.id })} data-teste={`replanejar-item-${i.ordem}`}>
                  Prazo da previsão {i.ordem}/{i.total}
                </button>
              ))}
            </p>
          )}
        </div>
      </details>
      <details className="rc-recolhido" data-teste="composicao">
        <summary>Composição da estimativa</summary>
        <div>
          <Composicao f={f} />
        </div>
      </details>
      {modal?.tipo === 'atualizar' && <AtualizarParcela f={f} itemId={modal.item} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'estorno' && <EstornarPagamento f={f} id={modal.id} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'resgate' && <SolicitarResgate f={f} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'estorno_reconhecimento' && <ComJustificativa titulo={`Estornar reconhecimento — ${f.numero}`} rotulo="Estornar reconhecimento" caminho={`/api/receber/comissoes/${f.numero}/reconhecimentos/${modal.id}/estorno`} aviso="Registro incorreto: o reconhecimento sai da previsão (volta a ser projetado); o registro fica no histórico." aoFechar={fechar} aoSalvar={salvo} teste="estorno-reconhecimento" />}
      {modal?.tipo === 'cancelar_resgate' && <ComJustificativa titulo={`Cancelar solicitação ${modal.numero}`} rotulo="Cancelar solicitação" caminho={`/api/receber/resgates/${modal.numero}/cancelar`} aviso="Registro incorreto: os itens voltam a ter a solicitação de resgate projetada; o registro fica no histórico." aoFechar={fechar} aoSalvar={salvo} teste="cancelar-resgate" />}
      {modal?.tipo === 'condicao' && <AjustarCondicao f={f} sugestaoInicial={modal.sugestao} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'redistribuir' && <Redistribuir f={f} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'calendario' && <InformarCalendario f={f} modo={modal.modo} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'previsao' && <Replanejar f={f} item={modal.item} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'referencia' && <PedidoInterno f={f} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'cancelar' && <CancelarComissao f={f} aoFechar={fechar} aoSalvar={salvo} />}
      {modal?.tipo === 'nota' && <EmitirNota inicial={{ comissao: f.numero }} aoFechar={fechar} aoSalvar={(n) => navegar(`/contas-receber/notas/${n}`)} />}
    </>
  );
}

type ModoCalendario = 'lista' | 'sugestao' | 'complemento';
const ROTULO_VALOR: Record<ParcelaCalendario['origemValor'], string> = { documentado: 'documentado', sugestao_igual: 'divisão igual · sugestão' };
/**
 * Venda: calendário do Cliente — o que existe, o que falta, a SUGESTÃO pela condição vigente no envio (só preenche a
 * janela) e o diagnóstico antecipado do que dá para fazer (motivo específico por parcela; nada genérico). O "Prazo
 * geral" da Proposta é PRAZO DE ENTREGA: aparece separado, nunca como condição de pagamento.
 */
function CalendarioCliente({ f, podeInformar, aoInformar }: { f: Ficha; podeInformar: boolean; aoInformar: (modo: ModoCalendario) => void }) {
  const k = f.calendario!;
  const dg = f.diagnosticoCalendario;
  const dias = (d: number[]) => `${d.length} parcela${d.length > 1 ? 's' : ''} · ${d.join('/')} dias da NF`;
  const pode = podeInformar && !k.estruturado;
  return (
    <div className="rc-caixa rc-calendario" data-teste="calendario-cliente">
      <h3>
        Calendário do Cliente
        <Ajuda titulo="Calendário do Cliente">As parcelas do Cliente vêm só dos prazos congelados no envio da Proposta ou de um calendário informado aqui, com origem. A sugestão usa a condição como era no envio da Proposta (a versão do cadastro em vigor naquela data); o texto da condição nunca vira calendário sozinho. O calendário é diferente da liberação da comissão, que segue a condição de liberação.</Ajuda>
      </h3>
      <div className="rc-calendario-grade">
        <div>
          <b>O que existe</b>
          <ul className="rc-lista">
            <li>Condição: {k.condicao || '—'}{k.condicaoId ? ` (${k.condicaoId})` : ''}</li>
            {k.valorFaturado && <li>Valor faturado: {rs(k.valorFaturado)}{k.dataNf ? ` · NF de ${dataBr(k.dataNf)}` : ''}</li>}
            {k.envioProposta && <li>Proposta enviada em {dataBr(k.envioProposta)}</li>}
            {k.congelado && <li data-teste="prazos-congelados">Prazos congelados no envio ({dataBr(k.congelado.copiadoEm)}): {dias(k.congelado.dias)}</li>}
            {k.informado && (
              <li data-teste="calendario-informado">
                Informado em {dataHora(k.informado.em)}: {k.informado.forma === 'complemento_vencimento' ? 'vencimento da parcela única completado' : `${k.informado.parcelas} parcela${k.informado.parcelas === 1 ? '' : 's'}`} · {ROTULO_ORIGEM_DISTRIBUICAO[k.informado.origem] ?? k.informado.origem}
                {k.informado.documento ? ` (${k.informado.documento})` : ''}
                {k.informado.detalhe && k.informado.detalhe.length > 0 && (
                  <span className="rc-sec">{k.informado.detalhe.map((x) => `${dataBr(x.vencimento)}${x.vencimentoSugerido ? ` (editado; sugestão ${dataBr(x.vencimentoSugerido)})` : ''} · ${rs(x.valor)} (${ROTULO_VALOR[x.origemValor]})`).join(' · ')}</span>
                )}
              </li>
            )}
          </ul>
        </div>
        <div>
          <b>O que falta</b>
          {k.falta.length ? (
            <ul className="rc-lista" data-teste="calendario-falta">
              {k.falta.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : (
            <p className="rc-sec sem-margem">nada: calendário estruturado</p>
          )}
        </div>
      </div>
      {!k.estruturado && k.sugestao && (
        <div className="rc-sugestao-cal" data-teste="sugestao-calendario">
          <b>Calendário sugerido</b> {selo(k.sugestao.fonte === 'condicao_envio' ? 'condição vigente no envio' : 'cadastro atual · não vigente no envio', k.sugestao.fonte === 'condicao_envio' ? 'ok' : 'pend')}
          <span className="rc-sec">
            {k.sugestao.condicao.nome} ({k.sugestao.condicao.dias.join('/')} dias da NF) · {k.sugestao.documento}
          </span>
          {k.sugestao.aviso && <span className="rc-sec rc-alerta-txt">{k.sugestao.aviso}</span>}
          <ul className="rc-lista">
            {k.sugestao.parcelas.map((x, i) => (
              <li key={i}>
                {x.prazoDias} dias · {dataBr(x.vencimento)} · {rs(x.valor)} <span className="rc-sec rc-inline">({ROTULO_VALOR[x.origemValor]})</span>
              </li>
            ))}
          </ul>
          <span className="rc-sec">A sugestão só preenche a janela: os valores por divisão igual precisam ser conferidos com o documento, e quem grava é você, com motivo.</span>
        </div>
      )}
      {!k.estruturado && !k.sugestao && k.semSugestao && <p className="rc-sec sem-margem" data-teste="sem-sugestao">Sem calendário sugerido: {k.semSugestao}</p>}
      {k.observacaoCondicao && (
        <p className="rc-sec sem-margem" data-teste="texto-condicao">
          Texto da condição na Proposta (só referência; não vira calendário): “{k.observacaoCondicao}”
        </p>
      )}
      {!k.estruturado && dg.mensagem && (
        <div className={`aviso ${dg.bloqueio || (!dg.podeDividir && !dg.podeCompletarVencimento) ? 'aviso-erro' : 'aviso-atencao-fin'}`} data-teste="diagnostico-calendario">
          {dg.mensagem}
        </div>
      )}
      {pode && (dg.podeDividir || dg.podeCompletarVencimento) && (
        <div className="rc-linha-botoes">
          {dg.podeDividir && k.sugestao && (
            <button type="button" className="botao-primario" onClick={() => aoInformar('sugestao')} data-teste="usar-sugestao">
              Conferir e usar a sugestão
            </button>
          )}
          <button type="button" className="botao-secundario" onClick={() => aoInformar(dg.podeDividir ? 'lista' : 'complemento')} data-teste="informar-calendario">
            {dg.podeDividir ? (k.sugestao ? 'Informar outro calendário' : 'Informar calendário do Cliente') : 'Completar o vencimento da parcela'}
          </button>
        </div>
      )}
      {k.prazoGeral && (
        <p className="rc-sec sem-margem rc-prazo-entrega" data-teste="prazo-entrega">
          Prazo de entrega (campo “Prazo geral” da Proposta): {k.prazoGeral} · não é condição de pagamento e não entra no calendário.
        </p>
      )}
    </div>
  );
}

/**
 * Calendário completo da comissão, previsto × real, por parcela: vencimento do Cliente, pagamento efetivo, liberação
 * (prevista ou efetiva), recebimento esperado e efetivo, original × atual, diferença de valor (confirmado − estimado)
 * e o histórico das atualizações automáticas e manuais, com o motivo.
 */
function CalendarioPrevisao({ f }: { f: Ficha }) {
  const dia = (d: { data: string | null; estimada: boolean }, efetivo = 'efetiva') => (d.data ? `${dataBr(d.data)} · ${d.estimada ? 'estimada' : efetivo}` : '—');
  const historico = f.itens.flatMap((i) => i.previsao.historico.map((h) => ({ ...h, item: `${i.ordem}/${i.total}` }))).sort((a, b) => a.em.localeCompare(b.em));
  return (
    <details className="rc-recolhido" data-teste="calendario-previsao">
      <summary>Calendário previsto × real</summary>
      <div>
        <RolagemIndicada className="rolagem quadro-tabela" teste="tabela-calendario">
          <table className="tabela tabela-compacta rc-fixo">
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Parcela</th>
                <th style={{ width: '14%' }}>Pagamento do Cliente</th>
                <th style={{ width: '15%' }}>Liberação</th>
                <th style={{ width: '15%' }}>Recebimento</th>
                <th style={{ width: '11%' }}>Original</th>
                <th className="numero" style={{ width: '12%' }}>
                  Dif. de valor
                </th>
                <th style={{ width: '23%' }}>Como foi calculada</th>
              </tr>
            </thead>
            <tbody>
              {f.itens.map((i) => {
                const pr = i.previsao.projecao;
                const dif = i.confirmado && i.estimado !== null ? Number(i.confirmado.valor) - Number(i.estimado) : null;
                return (
                  <tr key={i.id} data-teste={`calendario-${i.ordem}`}>
                    <td className="rc-num">
                      {i.ordem}/{i.total}
                    </td>
                    <td className="rc-num">
                      {dia(pr.pagamentoCliente, 'efetivo')}
                      {pr.pagamentoCliente.parcial && <span className="rc-sec">parcial: não libera</span>}
                    </td>
                    <td className="rc-num">
                      {dia(pr.liberacao)}
                      {pr.reconhecimento && <span className="rc-sec">reconhec. {dia(pr.reconhecimento, 'registrado')}</span>}
                      {pr.resgate && <span className="rc-sec">resgate {i.resgate ? `${dataBr(i.resgate.data)} · ${i.resgate.numero}` : `${dataBr(pr.resgate.data)} · projetado`}</span>}
                    </td>
                    <td className="rc-num">
                      {pr.recebimento.data ? dia(pr.recebimento, 'recebido') : <span className="rc-sec">falta: {pr.falta.join('; ')}</span>}
                      {i.previsao.negociada && <span className="rc-sec">negociado {dataBr(i.previsao.negociada.data)}</span>}
                    </td>
                    <td className="rc-num">{dataBr(i.previsao.original)}</td>
                    <td className="numero rc-num">{dif === null ? '—' : `${dif > 0 ? '+' : ''}${rs(dif.toFixed(2))}`}</td>
                    <td className="texto-secundario">{pr.fundamento || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RolagemIndicada>
        {historico.length > 0 && (
          <ul className="rc-hist" data-teste="historico-previsao">
            {historico.map((h, n) => (
              <li key={n}>
                <span>{dataHora(h.em)}</span>
                <span>
                  Parcela {h.item}: {h.origem === 'retorno_calculo' ? 'volta à data calculada' : h.previsao ? dataBr(h.previsao) : `sem previsão (falta: ${h.falta ?? '—'})`} · {h.motivo}
                  {h.origem === 'automatica' ? ' · cálculo automático' : h.origem === 'negociada' ? ` · prazo negociado${h.por ? ` por ${h.por}` : ''}` : h.por ? ` · por ${h.por}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function Composicao({ f }: { f: Ficha }) {
  const ab = f.base.abatimento === null || f.base.abatimento === undefined ? '—' : `${String(f.base.abatimento).replace('.', ',')}%`;
  if (f.origemTipo === 'nota_parceiro')
    return (
      <>
        <table className="rc-calc" data-teste="calculo-nota">
          <tbody>
            <tr>
              <td>Valor faturado nesta NF</td>
              <td>{rs(f.base.valorFaturado ?? 0)}</td>
            </tr>
            <tr>
              <td>Margem dos itens desta NF (preço − custo)</td>
              <td>{rs(f.base.margem ?? 0)}</td>
            </tr>
            <tr>
              <td>Frete atribuído à OCF, rateado pelo valor desta NF</td>
              <td>− {rs(f.base.frete ?? 0)}</td>
            </tr>
            <tr>
              <td>Comissão após o frete (desta NF)</td>
              <td>{rs(f.base.baseAposFrete ?? 0)}</td>
            </tr>
            <tr className="total">
              <td>Estimativa desta comissão (abatimento de {ab} uma única vez)</td>
              <td>{valorOuConfirmar(f.estimativa)}</td>
            </tr>
          </tbody>
        </table>
        {f.estimativaAtual !== null && f.estimativa !== null && f.estimativaAtual !== f.estimativa && <p className="rc-alerta atencao">Recalculada hoje, esta nota daria {rs(f.estimativaAtual)} (o Comercial mudou a operação depois da criação). Nada foi alterado automaticamente.</p>}
        {f.operacao?.composicao && (
          <>
            <h3 className="rc-subtitulo">
              Operação inteira
              <Ajuda titulo="Operação inteira">Fonte: a comissão após o frete já calculada no Comercial, com o abatimento aplicado uma única vez. A estimativa da operação é repartida entre as notas, o saldo cancelado e o backlog; a soma é exata.</Ajuda>
            </h3>
            <div className="rolagem">
              <table className="tabela tabela-compacta" data-teste="composicao-operacao">
                <thead>
                  <tr>
                    <th>Parte</th>
                    <th className="numero">Margem</th>
                    <th className="numero">Frete</th>
                    <th className="numero">Após o frete</th>
                    <th className="numero">Estimativa</th>
                  </tr>
                </thead>
                <tbody>
                  {f.operacao.composicao.map((c) => (
                    <tr key={c.parte}>
                      <td>{c.parte}</td>
                      <td className="numero">{rs(c.margem)}</td>
                      <td className="numero">{rs(c.frete)}</td>
                      <td className="numero">{rs(c.baseAposFrete)}</td>
                      <td className="numero">{valorOuConfirmar(c.estimativa)}</td>
                    </tr>
                  ))}
                  <tr className="rc-total">
                    <td>Total da operação</td>
                    <td className="numero" />
                    <td className="numero" />
                    <td className="numero">{rs(f.operacao.comissaoAposFrete ?? 0)}</td>
                    <td className="numero">{valorOuConfirmar(f.operacao.estimativa)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  return (
    <table className="rc-calc" data-teste="calculo-comissao">
      <tbody>
        {f.modalidade === 'locacao_mensal' && (
          <tr>
            <td>Mensalidade do Cliente × {f.base.percentual}%</td>
            <td>{rs(f.base.mensalidade ?? 0)}</td>
          </tr>
        )}
        {f.modalidade === 'locacao_projeto' && (
          <tr>
            <td>Comissão bruta do projeto ({f.base.percentual}% de {rs(f.base.totalContrato ?? 0)})</td>
            <td>{rs(f.base.comissaoBruta ?? 0)}</td>
          </tr>
        )}
        {f.modalidade === 'trade_in' && (
          <tr>
            <td>Comissão bruta (avaliação {rs(f.base.avaliacao ?? 0)} − oferta ao Cliente {rs(f.base.oferta ?? 0)})</td>
            <td>{rs(f.base.comissaoBruta ?? 0)}</td>
          </tr>
        )}
        <tr className="total">
          <td>Estimativa (abatimento de {ab} uma única vez)</td>
          <td>{valorOuConfirmar(f.estimativa)}</td>
        </tr>
        {f.modalidade === 'trade_in' && (
          <tr className="info">
            <td colSpan={2}>O valor dos equipamentos é pago pelo comprador direto ao Cliente: não movimenta o caixa da Inttrix.</td>
          </tr>
        )}
        {f.modalidade === 'locacao_mensal' && (
          <tr className="info">
            <td colSpan={2}>Unidade de criação: uma comissão por remessa da OL, com uma parcela por mensalidade; cada mensalidade libera sozinha após o pagamento do Cliente.</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PedidoInterno({ f, aoFechar, aoSalvar }: { f: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const [valor, setValor] = useState(f.pedidoInterno ?? '');
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/referencia`, aoSalvar);
  return (
    <Modal titulo={`Pedido interno do parceiro — ${f.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Salvar" pronto={valor !== (f.pedidoInterno ?? '')} enviando={env.enviando} aoConfirmar={() => void env.enviar({ pedido_interno: valor })} teste="salvar-referencia" />}>
      <Erro texto={env.erro} />
      <Campo rotulo="Pedido interno do parceiro (opcional)">{(id) => <input id={id} value={valor} maxLength={60} onChange={(e) => setValor(e.target.value)} data-teste="pedido-interno-input" />}</Campo>
      <p className="texto-secundario">Referência para localizar a comissão no sistema do parceiro. Não é etapa obrigatória; as próximas notas da mesma OCF herdam esta referência.</p>
    </Modal>
  );
}

function CancelarComissao({ f, aoFechar, aoSalvar }: { f: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const [just, setJust] = useState('');
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/cancelar`, aoSalvar);
  return (
    <Modal titulo={`Cancelar ${f.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Cancelar comissão" pronto={just.trim().length >= 10} enviando={env.enviando} aoConfirmar={() => void env.enviar({ justificativa: just })} teste="confirmar-cancelamento" />}>
      <Erro texto={env.erro} />
      <p>A comissão e o seu histórico ficam preservados, fora dos valores a receber. Comissão com parcela em nota ativa não é cancelada (cancele ou reemita a nota antes).</p>
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} data-teste="justificativa-cancelamento" />}</Campo>
    </Modal>
  );
}

// ---------------------------------------------------------------- prévia ao vivo (mesma escrita, desfeita no servidor)
function usePrevia<T>(caminho: string, corpo: Record<string, unknown> | null) {
  const [previa, setPrevia] = useState<T | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const chave = corpo ? JSON.stringify(corpo) : '';
  useEffect(() => {
    if (!corpo) {
      setPrevia(null);
      setErro(null);
      return;
    }
    let vivo = true;
    setCarregando(true);
    const t = setTimeout(() => {
      api
        .post<T>(caminho, { ...corpo, previa: true })
        .then((r) => vivo && (setPrevia(r), setErro(null)))
        .catch((e) => vivo && (setPrevia(null), setErro((e as Error).message)))
        .finally(() => vivo && setCarregando(false));
    }, 350);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caminho, chave]);
  return { previa, erro, carregando };
}
function useEnviar(caminho: string, aoSalvar: () => void) {
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enviar = async (corpo: Record<string, unknown>) => {
    setEnviando(true);
    setErro(null);
    try {
      await api.post(caminho, corpo);
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return { erro, enviando, enviar };
}
function Rodape({ aoFechar, rotulo, pronto, enviando, aoConfirmar, teste }: { aoFechar: () => void; rotulo: string; pronto: boolean; enviando: boolean; aoConfirmar: () => void; teste: string }) {
  return (
    <>
      <button type="button" className="botao-secundario" onClick={aoFechar}>
        Voltar
      </button>
      <button type="button" className="botao-primario" disabled={!pronto || enviando} aria-busy={enviando} onClick={aoConfirmar} data-teste={teste}>
        {rotulo}
      </button>
    </>
  );
}
const Erro = ({ texto }: { texto: string | null }) => (texto ? <div className="aviso aviso-erro" role="alert">{texto}</div> : null);
const rotuloLib: Record<SituacaoLiberacao, string> = { a_confirmar: 'Liberação a confirmar', aguardando_cliente: 'Aguardando Cliente', aguardando_liberacao: 'Aguardando liberação', liberado: 'Liberado' };
const retratoLib = (r: Retrato) => (r.disponibilidade === 'disponivel_nota' ? 'Validado · disponível para nota' : r.disponibilidade === 'liberado_aguardando_confirmacao' ? 'Liberado · aguardando confirmação do valor' : rotuloLib[r.liberacao]);
const retratoRep = (r: Retrato) => (r.repasseVencimento ? `${r.repasse === 'vencido' ? 'vencido' : 'até'} ${dataBr(r.repasseVencimento)}` : r.repasse === 'aguardando_marco' ? 'aguardando marco' : 'a confirmar (sem vencimento)');

// ---------------------------------------------------------------- atualizar parcela (pagamento do Cliente e/ou confirmação)
interface PreviaAtualizar {
  previsao?: { antes: string | null; depois: string | null; negociada: string | null };
  pagoAntes: string;
  pagoDepois: string;
  saldoAntes: string | null;
  saldoDepois: string | null;
  liberacao: string;
  disponibilidade: string | null;
  diferenca: string | null;
  datas: { efetiva: string; registro: string } | null;
  texto: string;
}
function AtualizarParcela({ f, itemId, aoFechar, aoSalvar }: { f: Ficha; itemId: string; aoFechar: () => void; aoSalvar: () => void }) {
  const i = f.itens.find((x) => x.id === itemId)!;
  const par = f.parcelas.find((x) => x.item === itemId);
  const trade = f.modalidade === 'trade_in';
  const pago = Number(par?.pago ?? 0);
  const base = i.base === null ? null : Number(i.base);
  const saldo = base === null ? null : Math.max(0, base - pago);
  const p = f.permissoes;
  const [comPagamento, setComPagamento] = useState(p.registrarPagamento && (saldo === null || saldo > 0));
  const [valor, setValor] = useState(saldo !== null && saldo > 0 ? saldo.toFixed(2).replace('.', ',') : '');
  const [data, setData] = useState(hojeIso());
  // origem e documento COMUNS dos fatos marcados; cada fato pode usar outra origem (null = a comum)
  const [origemComum, setOrigemComum] = useState(trade ? 'comprovante_cliente' : 'informado_parceiro');
  const [docComum, setDocComum] = useState('');
  const [origemPg, setOrigemPg] = useState<string | null>(null);
  const [comConfirmacao, setComConfirmacao] = useState(false);
  const [confirmado, setConfirmado] = useState((i.confirmado?.valor ?? i.estimado ?? '').replace('.', ','));
  const [origemConf, setOrigemConf] = useState<string | null>(null);
  const [documento, setDocumento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [chave] = useState(novaChave);
  const correcao = Boolean(i.confirmado);
  // reconhecimento REAL do pagamento pelo parceiro: só liberação por pagamento, com a parcela quitada (o prazo só projeta)
  const porPagamento = ['proporcional', 'integral_primeira', 'mensalidade'].includes(i.liberacao.condicao.tipo ?? '');
  const quitaAgora = saldo === 0 || (comPagamento && saldo !== null && centavosDe(valor) >= Math.round(saldo * 100));
  const podeReconhecer = porPagamento && p.registrarPagamento && !i.reconhecimentoPagamento && quitaAgora;
  const [comReconhecimento, setComReconhecimento] = useState(false);
  const [dataRec, setDataRec] = useState(hojeIso());
  const [origemRec, setOrigemRec] = useState<string | null>(null);
  const oPg = origemPg ?? origemDoFato(origemComum, 'pagamento');
  const oRec = origemRec ?? origemDoFato(origemComum, 'reconhecimento');
  const oConf = origemConf ?? origemDoFato(origemComum, 'confirmacao');
  const recOk = comReconhecimento && podeReconhecer && Boolean(dataRec) && Boolean(oRec);
  const pgOk = comPagamento && centavosDe(valor) > 0 && Boolean(data) && Boolean(oPg);
  const cfOk = comConfirmacao && confirmado.trim() !== '' && centavosDe(confirmado) >= 0 && (!correcao || justificativa.trim().length >= 10) && Boolean(oConf);
  const evidencia = docComum.trim() || undefined;
  const corpo =
    (comPagamento && !pgOk) || (comConfirmacao && !cfOk) || (comReconhecimento && !recOk) || (!comPagamento && !comConfirmacao && !comReconhecimento)
      ? null
      : {
          pagamento: comPagamento ? { valor: (centavosDe(valor) / 100).toFixed(2), data, origem: oPg, evidencia } : undefined,
          reconhecimento: comReconhecimento ? { data: dataRec, origem: oRec, evidencia } : undefined,
          confirmacao: comConfirmacao ? { valor: (centavosDe(confirmado) / 100).toFixed(2), origem: oConf, documento: documento || evidencia, observacao: observacao || undefined, justificativa: correcao ? justificativa : undefined, substitui: i.confirmado?.id } : undefined,
        };
  const pv = usePrevia<PreviaAtualizar>(`/api/receber/comissoes/${f.numero}/itens/${itemId}/atualizar`, corpo);
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/itens/${itemId}/atualizar`, aoSalvar);
  const subtitulo = [f.numero, f.nf ? `NF do parceiro ${f.nf}` : f.documento, f.pedido].filter(Boolean).join(' · ');
  return (
    <Modal
      titulo={`Atualizar ${f.modalidade === 'locacao_mensal' ? 'mensalidade' : 'parcela'} ${i.ordem} / ${i.total}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          {pv.previa && (
            <span className="rc-efeito-salvar rc-rodape-info" data-teste="efeito-atualizar">
              {pv.previa.texto}
            </span>
          )}
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!pv.previa || env.enviando} aria-busy={env.enviando} onClick={() => void env.enviar({ ...corpo!, chave_idempotencia: chave })} data-teste="salvar-atualizacao">
            Salvar atualização
          </button>
        </>
      }
    >
      <p className="texto-secundario sem-margem">{subtitulo}</p>
      <Erro texto={env.erro ?? pv.erro} />
      <div className="grade-form duas" data-teste="origem-comum">
        <Campo rotulo="Origem da informação (vale para os fatos marcados)">
          {(id) => (
            <select id={id} value={origemComum} onChange={(e) => setOrigemComum(e.target.value)} data-teste="origem-comum-select">
              {Object.entries(ORIGEM_COMUM).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Documento ou referência (opcional, comum)">{(id) => <input id={id} value={docComum} maxLength={300} placeholder="Ex.: e-mail do parceiro de 20/10" onChange={(e) => setDocComum(e.target.value)} data-teste="documento-comum" />}</Campo>
      </div>
      <div className="rc-modal2">
        <section className="rc-secao" data-teste="secao-pagamento">
          <h3>{trade ? 'Pagamento do comprador ao cliente' : 'Pagamento do cliente final ao parceiro'}</h3>
          <p className="rc-sec sem-margem" data-teste="ja-pago">
            Já pago {rs(pago)}
            {saldo !== null ? ` · Saldo restante ${rs(saldo)}` : ''}
          </p>
          <label className="opcao">
            <input type="checkbox" checked={comPagamento} disabled={!p.registrarPagamento || saldo === 0} onChange={(e) => setComPagamento(e.target.checked)} data-teste="marcar-pagamento" /> Registrar pagamento
          </label>
          {comPagamento && (
            <>
              <div className="grade-form duas">
                <Campo rotulo="Valor pago">{(id) => <input id={id} inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} data-teste="valor-pago" />}</Campo>
                <CampoData rotulo="Data efetiva" valor={data} aoMudar={setData} max={hojeIso()} teste="data-efetiva" />
              </div>
              <OrigemFato fato="pagamento" comum={origemComum} propria={origemPg} aoMudar={setOrigemPg} teste="origem-pagamento" />
            </>
          )}
          <p className="rc-info">{trade ? 'Pagamento do comprador ao cliente. Não registra entrada na Inttrix.' : 'Pagamento do cliente final ao parceiro. Não registra entrada na Inttrix.'}</p>
          {pv.previa?.datas && pv.previa.datas.efetiva !== pv.previa.datas.registro && (
            <p className="rc-sec">
              Data efetiva {dataBr(pv.previa.datas.efetiva)} · registro {dataBr(pv.previa.datas.registro)}: a liberação usa a data efetiva.
            </p>
          )}
          {porPagamento && !trade && (
            <div className="rc-subsecao" data-teste="secao-reconhecimento">
              <label className="opcao">
                <input type="checkbox" checked={comReconhecimento} disabled={!podeReconhecer} onChange={(e) => setComReconhecimento(e.target.checked)} data-teste="marcar-reconhecimento" /> Registrar o reconhecimento do pagamento pelo parceiro
                <Ajuda titulo="Reconhecimento do pagamento">O prazo configurado só projeta quando o parceiro deve reconhecer o pagamento; passar os dias não confirma nada. Aqui você informa o reconhecimento real (data efetiva e origem), com a parcela do Cliente quitada.</Ajuda>
              </label>
              {i.reconhecimentoPagamento ? (
                <p className="rc-sec sem-margem">Reconhecido em {dataBr(i.reconhecimentoPagamento.data)} (para corrigir, estorne no histórico).</p>
              ) : !quitaAgora ? (
                <p className="rc-sec sem-margem">Disponível quando a parcela estiver quitada: pagamento parcial não é reconhecido nem libera.</p>
              ) : comReconhecimento ? (
                <div className="grade-form duas">
                  <CampoData rotulo="Data do reconhecimento" valor={dataRec} aoMudar={setDataRec} max={hojeIso()} teste="data-reconhecimento" />
                  <OrigemFato fato="reconhecimento" comum={origemComum} propria={origemRec} aoMudar={setOrigemRec} teste="origem-reconhecimento" />
                </div>
              ) : null}
              {pv.previa?.previsao && pv.previa.previsao.antes !== pv.previa.previsao.depois && (
                <p className="rc-sec" data-teste="efeito-previsao">
                  Data esperada calculada: {pv.previa.previsao.antes ? dataBr(pv.previa.previsao.antes) : 'sem dados'} → {pv.previa.previsao.depois ? dataBr(pv.previa.previsao.depois) : 'sem dados'}
                  {pv.previa.previsao.negociada ? ` (o prazo negociado ${dataBr(pv.previa.previsao.negociada)} continua valendo)` : ''}
                </p>
              )}
            </div>
          )}
        </section>
        <section className="rc-secao" data-teste="secao-confirmacao">
          <h3>
            Valor da comissão <span className="texto-secundario">(opcional)</span>
          </h3>
          <label className="opcao">
            <input type="checkbox" checked={comConfirmacao} disabled={!p.confirmarValor} onChange={(e) => setComConfirmacao(e.target.checked)} data-teste="marcar-confirmacao" /> {correcao ? 'Corrigir o valor confirmado' : 'Confirmar valor do parceiro'}
          </label>
          {comConfirmacao && (
            <>
              <div className="grade-form duas">
                <Campo rotulo="Estimado">{(id) => <input id={id} value={valorOuConfirmar(i.estimado)} readOnly className="rc-campo-ro" />}</Campo>
                <Campo rotulo="Confirmado">{(id) => <input id={id} inputMode="decimal" value={confirmado} onChange={(e) => setConfirmado(e.target.value)} data-teste="valor-confirmado" />}</Campo>
                <Campo rotulo="Documento deste fato (opcional)">{(id) => <input id={id} value={documento} maxLength={300} placeholder={docComum ? `comum: ${docComum}` : 'Ex.: NF, e-mail, mensagem…'} onChange={(e) => setDocumento(e.target.value)} />}</Campo>
                <Campo rotulo="Observação (opcional)">{(id) => <input id={id} value={observacao} maxLength={500} placeholder="Ex.: ajuste conforme negociação…" onChange={(e) => setObservacao(e.target.value)} />}</Campo>
              </div>
              <OrigemFato fato="confirmacao" comum={origemComum} propria={origemConf} aoMudar={setOrigemConf} teste="origem-confirmacao" />
            </>
          )}
          {correcao && comConfirmacao && <Campo rotulo="Justificativa da correção (mínimo 10 caracteres)">{(id) => <input id={id} value={justificativa} maxLength={500} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-correcao" />}</Campo>}
          {i.confirmado && (
            <p className="rc-sec">
              Confirmado hoje: {rs(i.confirmado.valor)} em {dataBr(i.confirmado.data)} · {ORIGEM_CONF[i.confirmado.origem] ?? i.confirmado.origem}
            </p>
          )}
          {pv.previa?.diferenca && Number(pv.previa.diferenca) !== 0 && <p className="rc-sec">Diferença para a estimativa: {rs(pv.previa.diferenca)} (não gera crédito).</p>}
        </section>
      </div>
    </Modal>
  );
}

function TabelaEfeitos({ efeitos, vazio, acao }: { efeitos: Efeito[]; vazio: string; acao?: (e: Efeito) => ReactNode }) {
  if (!efeitos.length) return <p className="texto-secundario">{vazio}</p>;
  return (
    <RolagemIndicada className="rolagem quadro-tabela" teste="efeitos">
      <table className="tabela tabela-compacta rc-fixo rc-efeito" data-teste="tabela-efeitos">
        <thead>
          <tr>
            <th style={{ width: acao ? '16%' : '20%' }}>Item</th>
            <th style={{ width: acao ? '26%' : '40%' }}>Antes</th>
            <th style={{ width: acao ? '30%' : '40%' }}>Depois</th>
            {acao && <th style={{ width: '28%' }}>Ação</th>}
          </tr>
        </thead>
        <tbody>
          {efeitos.map((e) => (
            <tr key={e.id}>
              <td>
                <strong>{e.rotulo.split('· ').pop()}</strong>
                <span className="rc-sec">{e.parcela}</span>
              </td>
              <td>
                {e.antes ? (
                  <>
                    {retratoLib(e.antes)}
                    {e.antes.liberacaoData ? ` ${dataBr(e.antes.liberacaoData)}` : ''}
                    <span className="rc-sec">repasse {retratoRep(e.antes)} · {valorOuConfirmar(e.antes.estimado)}</span>
                  </>
                ) : (
                  <span className="rc-tag">novo</span>
                )}
              </td>
              <td>
                <strong>{retratoLib(e.depois)}</strong>
                {e.depois.liberacaoData ? ` ${dataBr(e.depois.liberacaoData)}` : ''}
                <span className="rc-sec">
                  repasse {retratoRep(e.depois)} · {valorOuConfirmar(e.depois.estimado)}
                </span>
                {e.depois.liberacao === 'liberado' && e.depois.liberacaoFato && <span className="rc-sec">{e.depois.liberacaoFato}</span>}
              </td>
              {acao && <td>{acao(e)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </RolagemIndicada>
  );
}

// ---------------------------------------------------------------- estornar pagamento
function EstornarPagamento({ f, id, aoFechar, aoSalvar }: { f: Ficha; id: string; aoFechar: () => void; aoSalvar: () => void }) {
  const pg = f.pagamentos.find((x) => x.id === id)!;
  const [just, setJust] = useState('');
  const pv = usePrevia<{ efeitos: Efeito[]; aviso: string }>(`/api/receber/comissoes/${f.numero}/pagamentos/${id}/estorno`, {});
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/pagamentos/${id}/estorno`, aoSalvar);
  return (
    <Modal titulo={`Estornar pagamento do Cliente — ${f.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Estornar pagamento" pronto={just.trim().length >= 10 && Boolean(pv.previa)} enviando={env.enviando} aoConfirmar={() => void env.enviar({ justificativa: just })} teste="confirmar-estorno" />}>
      <Erro texto={env.erro ?? pv.erro} />
      <p>
        {f.parcelas.find((x) => x.chave === pg.parcela)?.rotulo ?? pg.parcela} · {rs(pg.valor)} em {dataBr(pg.data)}
      </p>
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(i) => <textarea id={i} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} data-teste="justificativa-estorno" />}</Campo>
      {pv.previa && (
        <>
          <TabelaEfeitos efeitos={pv.previa.efeitos} vazio="Nenhum item muda de situação." />
          <p className="texto-secundario">{pv.previa.aviso}</p>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- ajustar condição
type Sugestao = Ficha['sugestoes'][number];
function AjustarCondicao({ f, sugestaoInicial, aoFechar, aoSalvar }: { f: Ficha; sugestaoInicial?: string; aoFechar: () => void; aoSalvar: () => void }) {
  const c = f.condicao;
  const s0 = f.sugestoes.find((x) => x.id === sugestaoInicial);
  const numTxt = (x: unknown) => (x === null || x === undefined ? '' : String(x));
  const libDe = (v: Record<string, unknown>) => ({ tipo: String(v.tipo ?? ''), marco: String(v.marco ?? ''), dias: numTxt(v.dias), tipoDias: (v.tipoDias as TipoDias | undefined) ?? 'corridos', recDias: numTxt(v.reconhecimentoDias), recTipo: (v.reconhecimentoTipoDias as TipoDias | undefined) ?? 'corridos' });
  const repDe = (v: Record<string, unknown>) => ({ marco: String(v.marco ?? ''), descricao: String(v.descricao ?? ''), dias: numTxt(v.dias), tipoDias: (v.tipoDias as TipoDias | undefined) ?? 'corridos' });
  const [lib, setLibBruto] = useState(s0?.campo === 'liberacao' ? libDe(s0.valor) : libDe(c.liberacao as unknown as Record<string, unknown>));
  const [rep, setRepBruto] = useState(s0?.campo === 'repasse' ? repDe(s0.valor) : repDe(c.repasse as unknown as Record<string, unknown>));
  const [confirmacao, setConfirmacaoBruta] = useState(s0?.campo === 'confirmacao' ? String(s0.valor.valor ?? '') : (c.confirmacao.valor ?? ''));
  // a sugestão só vale como "informação usada" se a pessoa a escolheu e não mudou o campo depois
  const [origemSug, setOrigemSug] = useState<Record<string, string>>(s0 ? { [s0.campo]: s0.origem } : {});
  const semSug = (campo: string) => setOrigemSug((o) => Object.fromEntries(Object.entries(o).filter(([k]) => k !== campo)));
  const setLib = (v: typeof lib) => (setLibBruto(v), semSug('liberacao'));
  const setRep = (v: typeof rep) => (setRepBruto(v), semSug('repasse'));
  const setConfirmacao = (v: string) => (setConfirmacaoBruta(v), semSug('confirmacao'));
  const usar = (x: Sugestao) => {
    if (x.campo === 'liberacao') setLibBruto(libDe(x.valor));
    else if (x.campo === 'repasse') setRepBruto(repDe(x.valor));
    else setConfirmacaoBruta(String(x.valor.valor ?? ''));
    setOrigemSug((o) => ({ ...o, [x.campo]: x.origem }));
  };
  const [just, setJust] = useState('');
  const [marcados, setMarcados] = useState<string[]>([]);
  const [aplicarOperacao, setAplicarOperacao] = useState(false);
  const [escolhidas, setEscolhidas] = useState<string[]>(f.irmas.map((x) => x.numero));
  const precisaDias = lib.tipo === 'marco_prazo' || lib.tipo === 'dias_apos_pagamento';
  const porPagamento = lib.tipo === 'proporcional' || lib.tipo === 'integral_primeira' || lib.tipo === 'mensalidade';
  const corpo = useMemo(
    () => ({
      liberacao: {
        tipo: lib.tipo || null,
        marco: lib.tipo === 'marco_prazo' ? lib.marco || null : null,
        dias: precisaDias && lib.dias !== '' ? Number(lib.dias) : null,
        tipoDias: precisaDias ? (lib.tipoDias as TipoDias) : null,
        reconhecimentoDias: porPagamento && lib.recDias !== '' ? Number(lib.recDias) : null,
        reconhecimentoTipoDias: porPagamento && lib.recDias !== '' ? (lib.recTipo as TipoDias) : null,
      },
      repasse: { marco: rep.marco || null, descricao: rep.marco === 'outro' ? rep.descricao : '', dias: rep.dias === '' ? null : Number(rep.dias), tipoDias: rep.tipoDias as TipoDias },
      confirmacao: confirmacao || null,
      justificativa: just || undefined,
      aplicar_repasse_liberados: marcados,
      aplicar_operacao: aplicarOperacao && escolhidas.length > 0,
      comissoes: escolhidas,
      origem_sugestao: origemSug,
    }),
    [lib, rep, confirmacao, just, marcados, precisaDias, aplicarOperacao, escolhidas, origemSug],
  );
  const pv = usePrevia<{ tipo: 'definicao' | 'ajuste'; exigeJustificativa: boolean; comissoes: string[]; atual: { liberacao: string; repasse: string }; nova: { liberacao: string; repasse: string }; campos: string[]; efeitos: Efeito[]; liberamAgora: number; aviso: string }>(`/api/receber/comissoes/${f.numero}/condicao`, { ...corpo, justificativa: undefined });
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/condicao`, aoSalvar);
  const mudouRepasse = pv.previa?.campos.includes('Prazo de repasse');
  return (
    <Modal titulo={`Condições da operação — ${f.numero} · ${f.pedido}`} aoFechar={aoFechar} largo rodape={<Rodape aoFechar={aoFechar} rotulo={pv.previa?.tipo === 'definicao' ? 'Definir condição' : 'Aplicar ajuste'} pronto={Boolean(pv.previa) && (!pv.previa?.exigeJustificativa || just.trim().length >= 10)} enviando={env.enviando} aoConfirmar={() => void env.enviar(corpo)} teste="confirmar-condicao" />}>
      <Erro texto={env.erro} />
      <div className="rc-grade2">
        <div className="rc-caixa">
          <h3>Condição atual</h3>
          <table className="rc-campos">
            <tbody>
              <tr>
                <td>Liberação</td>
                <td>
                  {textoLiberacao(c.liberacao)}
                  <span className="rc-origem">{origemTexto(c.liberacao)}</span>
                </td>
              </tr>
              <tr>
                <td>Repasse</td>
                <td>
                  {textoRepasse(c.repasse)}
                  <span className="rc-origem">{origemTexto(c.repasse)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="rc-caixa">
          <h3>Nova condição desta operação</h3>
          <div className="grade-form duas">
            <Campo rotulo="Liberação">
              {(id) => (
                <select id={id} value={lib.tipo} onChange={(e) => setLib({ ...lib, tipo: e.target.value })} data-teste="nova-liberacao">
                  <option value="">A confirmar</option>
                  {TIPOS_LIBERACAO.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.rotulo}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            {lib.tipo === 'marco_prazo' ? (
              <Campo rotulo="Marco da liberação">
                {(id) => (
                  <select id={id} value={lib.marco} onChange={(e) => setLib({ ...lib, marco: e.target.value })} data-teste="marco-liberacao">
                    <option value="">A confirmar</option>
                    {MARCOS_LIBERACAO.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.rotulo}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
            ) : (
              <span />
            )}
            {porPagamento && (
              <>
                <Campo rotulo="Reconhecimento do pagamento (dias)" dica={<Ajuda titulo="Reconhecimento do pagamento">Dias entre o pagamento do Cliente ao parceiro e o parceiro reconhecer esse pagamento (ex.: boleto 30 dias + 2 = comissão prevista para NF + 32). Se o repasse partir do reconhecimento, os dias não são somados duas vezes.</Ajuda>}>
                  {(id) => <input id={id} inputMode="numeric" value={lib.recDias} placeholder="A confirmar" onChange={(e) => setLib({ ...lib, recDias: e.target.value.replace(/\D/g, '') })} data-teste="dias-reconhecimento" />}
                </Campo>
                <Campo rotulo="Tipo de dias (reconhecimento)">
                  {(id) => (
                    <select id={id} value={lib.recTipo} onChange={(e) => setLib({ ...lib, recTipo: e.target.value as TipoDias })} data-teste="tipo-dias-reconhecimento">
                      <option value="corridos">Corridos</option>
                      <option value="uteis">Úteis</option>
                    </select>
                  )}
                </Campo>
              </>
            )}
            {precisaDias && (
              <>
                <Campo rotulo="Dias da liberação">{(id) => <input id={id} inputMode="numeric" value={lib.dias} placeholder="A confirmar" onChange={(e) => setLib({ ...lib, dias: e.target.value.replace(/\D/g, '') })} data-teste="dias-liberacao" />}</Campo>
                <Campo rotulo="Tipo de dias (liberação)">
                  {(id) => (
                    <select id={id} value={lib.tipoDias} onChange={(e) => setLib({ ...lib, tipoDias: e.target.value as TipoDias })}>
                      <option value="corridos">Corridos</option>
                      <option value="uteis">Úteis</option>
                    </select>
                  )}
                </Campo>
              </>
            )}
            <Campo rotulo="Repasse: marco inicial">
              {(id) => (
                <select id={id} value={rep.marco} onChange={(e) => setRep({ ...rep, marco: e.target.value })} data-teste="marco-repasse">
                  <option value="">A confirmar</option>
                  {MARCOS_REPASSE.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.rotulo}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            {rep.marco === 'outro' && <Campo rotulo="Descrição do marco">{(id) => <input id={id} value={rep.descricao} maxLength={200} placeholder="Ex.: aceite do demonstrativo" onChange={(e) => setRep({ ...rep, descricao: e.target.value })} data-teste="descricao-marco-repasse" />}</Campo>}
            <Campo rotulo="Quantidade de dias">{(id) => <input id={id} inputMode="numeric" value={rep.dias} placeholder="A confirmar" onChange={(e) => setRep({ ...rep, dias: e.target.value.replace(/\D/g, '') })} data-teste="dias-repasse" />}</Campo>
            <Campo rotulo="Tipo de dias" dica={<Ajuda titulo="Dias úteis">Segunda a sexta. Feriados não são conhecidos pelo HUB: confira as datas quando houver feriado no período.</Ajuda>}>
              {(id) => (
                <select id={id} value={rep.tipoDias} onChange={(e) => setRep({ ...rep, tipoDias: e.target.value as TipoDias })} data-teste="tipo-dias-repasse">
                  <option value="corridos">Corridos</option>
                  <option value="uteis">Úteis</option>
                </select>
              )}
            </Campo>
            <Campo rotulo="Confirmação do valor">
              {(id) => (
                <select id={id} value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)}>
                  <option value="">A confirmar</option>
                  {ORIGENS_CONFIRMACAO.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.rotulo}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
          </div>
        </div>
      </div>
      {f.sugestoes.some((x) => !c[x.campo].origem) && (
        <div className="rc-sug" data-teste="sugestoes">
          <strong>Sugestões (só valem se você escolher; a origem fica registrada)</strong>
          {f.sugestoes
            .filter((x) => !c[x.campo].origem)
            .map((x) => (
              <span key={x.id}>
                {x.campo === 'liberacao' ? 'Liberação' : x.campo === 'repasse' ? 'Repasse' : 'Confirmação'}: {x.texto} <span className="texto-secundario">({x.detalhe})</span>{' '}
                {origemSug[x.campo] === x.origem ? (
                  <strong>· escolhida</strong>
                ) : (
                  <button type="button" className="botao-link" onClick={() => usar(x)} data-teste={`usar-${x.id}`}>
                    usar esta sugestão
                  </button>
                )}
              </span>
            ))}
        </div>
      )}
      {f.irmas.length > 0 && (
        <div className="rc-sug" data-teste="lote">
          <label>
            <input type="checkbox" checked={aplicarOperacao} onChange={(e) => setAplicarOperacao(e.target.checked)} data-teste="aplicar-operacao" /> Aplicar também a outras comissões desta operação (opcional)
          </label>
          {aplicarOperacao && (
            <>
              {f.irmas.map((x) => (
                <label key={x.numero} className="opcao">
                  <input type="checkbox" checked={escolhidas.includes(x.numero)} onChange={(e) => setEscolhidas(e.target.checked ? [...escolhidas, x.numero] : escolhidas.filter((y) => y !== x.numero))} data-teste={`lote-${x.numero}`} /> {x.numero}
                  {x.nf ? ` · NF ${x.nf}` : ''}
                </label>
              ))}
              <span className="texto-secundario">Os efeitos de cada comissão marcada aparecem abaixo. Parcelas liberadas, em nota da Inttrix ou quitadas não mudam.</span>
            </>
          )}
        </div>
      )}
      {pv.previa?.exigeJustificativa ? (
        <Campo rotulo="Justificativa (obrigatória para mudar uma condição já definida, mínimo 10 caracteres)">{(id) => <input id={id} value={just} maxLength={500} onChange={(e) => setJust(e.target.value)} data-teste="justificativa-condicao" />}</Campo>
      ) : (
        <p className="rc-sec">Completar um campo que estava “A confirmar” não exige justificativa: o histórico guarda quem definiu e quando.</p>
      )}
      <h3 className="rc-subtitulo">
        Prévia dos efeitos
        <Ajuda titulo="Prévia dos efeitos">Itens liberados não são recalculados: o repasse só muda neles com a sua marcação. Itens em nota ou quitados (a partir da E2) nunca mudam aqui; a prévia indicará a ação necessária. A comissão em si continua definida na Proposta.</Ajuda>
      </h3>
      {pv.erro && <p className="texto-secundario">{pv.erro}</p>}
      {pv.previa && (
        <>
          <TabelaEfeitos
            efeitos={pv.previa.efeitos}
            vazio="Nenhum item muda."
            acao={(e) =>
              e.liberadoAntes ? (
                mudouRepasse ? (
                  <label className="opcao">
                    <input type="checkbox" checked={marcados.includes(e.id)} onChange={(ev) => setMarcados(ev.target.checked ? [...marcados, e.id] : marcados.filter((x) => x !== e.id))} data-teste={`aplicar-repasse-${e.id}`} /> aplicar o novo repasse a este item liberado
                  </label>
                ) : (
                  <span className="texto-secundario">liberado: não muda</span>
                )
              ) : (
                '—'
              )
            }
          />
          <p className="faixa-resumo-modal">
            <span>
              Liberam agora: <strong>{plural(pv.previa.liberamAgora, 'item', 'itens')}</strong>
            </span>
            <span>Protegidos: {plural(pv.previa.efeitos.filter((e) => e.liberadoAntes).length, 'item liberado', 'itens liberados')}</span>
            <span>Comissão: não muda</span>
          </p>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- redistribuir
interface PreviaDistribuicao {
  tipo: 'redistribuicao' | 'calendario' | 'complemento_vencimento';
  total: string;
  protegido: string;
  redistribuido: string;
  somaExata: boolean;
  bases: { antes: string; depois: string; protegidas: string; novas: string; referencia: string; rotuloReferencia: string; fecha: boolean };
  residuo: { comissao: string; base: string | null; parcela: number };
  parcelas: { id: string; rotulo: string; parcela: string; situacao: 'protegida' | 'redistribuida' | 'sem_valor' | 'nova' | 'vencimento_completado'; motivo: string | null; baseAntes: string | null; baseDepois: string | null; estimadoAntes: string | null; estimadoDepois: string | null; vencimentoAntes: string | null; vencimentoDepois: string | null; primeira: boolean }[];
  prazosNegociados: { id: string; rotulo: string; negociado: string; motivo: string; novoVencimento: string | null; tratamento: TratamentoPrazo | null; novaData: string | null; previsaoDepois: string | null; calculadaDepois: string | null }[];
  escolhaPendente: string[];
  efeitosFatos: string[];
}
type Tratamentos = Record<string, { tratamento: TratamentoPrazo | ''; data: string }>;
const SELO_PARCELA: Record<PreviaDistribuicao['parcelas'][number]['situacao'], [string, 'ok' | 'pend' | 'ni' | 'ext']> = { protegida: ['protegida', 'ni'], redistribuida: ['redistribuída', 'ok'], sem_valor: ['sem valor', 'pend'], nova: ['nova', 'ext'], vencimento_completado: ['só o vencimento', 'ok'] };
const tratamentosDoCorpo = (t: Tratamentos) => Object.fromEntries(Object.entries(t).filter(([, x]) => x.tratamento && (x.tratamento !== 'nova_data' || x.data)).map(([id, x]) => [id, { tratamento: x.tratamento, data: x.tratamento === 'nova_data' ? x.data : undefined }]));
/** Parcelas antes → depois, com o motivo de cada proteção (nada muda nem some sem aparecer aqui). */
function TabelaDistribuicao({ pv }: { pv: PreviaDistribuicao }) {
  return (
    <RolagemIndicada className="rolagem quadro-tabela" teste="distribuicao">
      <table className="tabela tabela-compacta rc-fixo" data-teste="tabela-distribuicao">
        <thead>
          <tr>
            <th style={{ width: '24%' }}>Parcela</th>
            <th style={{ width: '28%' }}>Na redistribuição</th>
            <th style={{ width: '16%' }}>Vencimento</th>
            <th className="numero" style={{ width: '16%' }}>
              Base
            </th>
            <th className="numero" style={{ width: '16%' }}>
              Comissão
            </th>
          </tr>
        </thead>
        <tbody>
          {pv.parcelas.map((x) => (
            <tr key={x.id} data-teste={`dist-${x.situacao}`}>
              <td>
                <strong>{x.rotulo}</strong>
                {x.primeira && <span className="rc-tag">parcela 1</span>}
                <span className="rc-sec">{x.parcela}</span>
              </td>
              <td>
                {selo(SELO_PARCELA[x.situacao][0], SELO_PARCELA[x.situacao][1])}
                {x.motivo && <span className="rc-sec">{x.motivo}</span>}
              </td>
              <td>
                {x.vencimentoAntes !== x.vencimentoDepois && x.vencimentoAntes ? `${dataBr(x.vencimentoAntes)} → ` : ''}
                {x.vencimentoDepois ? dataBr(x.vencimentoDepois) : '—'}
              </td>
              <td className="numero">
                {x.baseAntes !== null && x.baseAntes !== x.baseDepois && <span className="rc-sec">{rs(x.baseAntes)} →</span>}
                {x.baseDepois === null ? '—' : rs(x.baseDepois)}
              </td>
              <td className="numero">
                {x.estimadoAntes !== null && x.estimadoAntes !== x.estimadoDepois && <span className="rc-sec">{rs(x.estimadoAntes)} →</span>}
                {valorOuConfirmar(x.estimadoDepois)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </RolagemIndicada>
  );
}
/** Escolha EXPLÍCITA do tratamento de cada prazo negociado (sem escolha, nada é gravado), com o efeito antes de confirmar. */
function EscolhaPrazos({ pv, valor, aoMudar }: { pv: PreviaDistribuicao; valor: Tratamentos; aoMudar: (t: Tratamentos) => void }) {
  if (!pv.prazosNegociados.length) return null;
  return (
    <div className="rc-prazos" data-teste="prazos-negociados">
      <h3 className="rc-subtitulo">
        Prazo negociado nas parcelas redistribuídas (escolha obrigatória)
        <Ajuda titulo="Prazo negociado">O prazo negociado nunca passa sozinho para outra parcela. Escolha o que fazer com ele; o histórico anterior continua registrado em qualquer caso.</Ajuda>
      </h3>
      {pv.prazosNegociados.map((x) => {
        const v = valor[x.id] ?? { tratamento: '', data: '' };
        const mudar = (n: Partial<typeof v>) => aoMudar({ ...valor, [x.id]: { ...v, ...n } });
        return (
          <fieldset key={x.id} className="rc-prazo" data-teste={`prazo-${x.rotulo.replace(' ', '-')}`}>
            <legend>
              {x.rotulo}: prazo negociado {dataBr(x.negociado)} <span className="rc-sec">({x.motivo})</span>
            </legend>
            {TRATAMENTOS_PRAZO.map((t) => (
              <label key={t.id} className="opcao">
                <input type="radio" name={`prazo-${x.id}`} checked={v.tratamento === t.id} onChange={() => mudar({ tratamento: t.id })} data-teste={`tratamento-${t.id}`} />
                {t.nome}
              </label>
            ))}
            {v.tratamento === 'nova_data' && <CampoData rotulo="Novo prazo negociado" valor={v.data} aoMudar={(d) => mudar({ data: d })} />}
            <p className="rc-sec" data-teste="efeito-prazo">
              {!x.tratamento
                ? 'Sem escolha: nada será gravado.'
                : x.tratamento === 'manter'
                  ? `Efeito: a data esperada continua ${dataBr(x.negociado)} (prazo negociado), com o novo vencimento ${x.novoVencimento ? dataBr(x.novoVencimento) : 'sem data'}.`
                  : x.tratamento === 'retorno_calculo'
                    ? `Efeito: a data esperada volta ao cálculo: ${x.calculadaDepois ? dataBr(x.calculadaDepois) : 'sem dados suficientes'}. O prazo de ${dataBr(x.negociado)} fica no histórico.`
                    : `Efeito: novo prazo negociado ${x.novaData ? dataBr(x.novaData) : '—'}; o de ${dataBr(x.negociado)} fica no histórico.`}
            </p>
          </fieldset>
        );
      })}
    </div>
  );
}
/** Totais para conferência (sempre visíveis no rodapé da janela). */
function TotaisDistribuicao({ pv, carregando, erro }: { pv: PreviaDistribuicao | null; carregando: boolean; erro?: string | null }) {
  // E0 (DF5): com erro na prévia, o rodapé não diz "preencha os dados" — diz que nada é gravado até corrigir
  if (!pv) return <span className="rc-totais texto-secundario">{carregando ? 'Calculando a prévia…' : erro ? 'Veja o motivo acima: nada é gravado assim.' : 'Preencha os dados para ver a prévia.'}</span>;
  if (pv.tipo === 'complemento_vencimento')
    return (
      <span className="rc-totais" data-teste="totais-distribuicao">
        <span>
          Só o vencimento muda: base <strong>{rs(pv.bases.depois)}</strong> e comissão <strong>{rs(pv.total)}</strong> continuam iguais ✓
        </span>
      </span>
    );
  return (
    <span className="rc-totais" data-teste="totais-distribuicao">
      <span>
        Bases: {rs(pv.bases.protegidas)} protegidas + {rs(pv.bases.novas)} novas = <strong>{rs(pv.bases.depois)}</strong> {pv.bases.fecha ? '✓' : '✗'} {pv.bases.rotuloReferencia} {rs(pv.bases.referencia)}
      </span>
      <span>
        Comissão: <strong>{rs(pv.total)}</strong> = antes {pv.somaExata ? '✓' : '✗'} · resíduo {rs(pv.residuo.comissao)} na última parcela nova
        {pv.residuo.base !== null ? ` · base ${rs(pv.residuo.base)}` : ''}
      </span>
    </span>
  );
}
function RodapeDistribuicao({ pv, carregando, erro, motivo, aoMotivo, aoFechar, rotulo, pronto, enviando, aoConfirmar, teste }: { pv: PreviaDistribuicao | null; carregando: boolean; erro?: string | null; motivo: string; aoMotivo: (m: string) => void; aoFechar: () => void; rotulo: string; pronto: boolean; enviando: boolean; aoConfirmar: () => void; teste: string }) {
  return (
    <div className="rc-rodape-dist">
      <TotaisDistribuicao pv={pv} carregando={carregando} erro={erro} />
      <div className="rc-rodape-linha">
        <Campo rotulo="Motivo *">{(id) => <input id={id} value={motivo} maxLength={500} onChange={(e) => aoMotivo(e.target.value)} data-teste={`motivo-${teste}`} />}</Campo>
        <div className="rc-rodape-botoes">
          <Rodape aoFechar={aoFechar} rotulo={rotulo} pronto={pronto} enviando={enviando} aoConfirmar={aoConfirmar} teste={`confirmar-${teste}`} />
        </div>
      </div>
    </div>
  );
}
const Bloqueio = ({ f }: { f: Ficha }) => (f.cancelamento || f.pedidoCancelado || f.origemDesfeita ? <div className="aviso aviso-erro">{f.cancelamento ? 'Comissão cancelada' : f.pedidoCancelado ? 'Pedido cancelado' : `Origem desfeita: ${f.origemDesfeita}`}: bloqueado.</div> : null);

function Redistribuir({ f, aoFechar, aoSalvar }: { f: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  // só as parcelas sem nenhum fato entram (as protegidas a prévia mostra com o motivo)
  const pagas = new Set(f.pagamentos.filter((x) => !x.estorno).map((x) => x.parcela));
  const livres = f.itens.filter((i) => i.liberacao.situacao !== 'liberado' && i.cobranca.situacao === 'nao_cobrado' && !i.cobranca.notas.length && !i.resgate && !i.confirmado && !i.reconhecimentoPagamento && !pagas.has(i.parcela));
  const [motivo, setMotivo] = useState('');
  const [origem, setOrigem] = useState('informado_parceiro');
  const [dataInfo, setDataInfo] = useState(hojeIso());
  const [parcelas, setParcelas] = useState(livres.map((i) => ({ base: i.base ? i.base.replace('.', ',') : '', vencimento: i.vencimentoParcela ?? '' })));
  const [tratamentos, setTratamentos] = useState<Tratamentos>({});
  const validas = parcelas.length > 0 && parcelas.every((p) => centavosDe(p.base) > 0);
  const corpo = validas ? { motivo: motivo || undefined, origem, data_info: dataInfo, parcelas: parcelas.map((p) => ({ base: (centavosDe(p.base) / 100).toFixed(2), vencimento: p.vencimento || null })), prazos_negociados: tratamentosDoCorpo(tratamentos) } : null;
  const pv = usePrevia<PreviaDistribuicao>(`/api/receber/comissoes/${f.numero}/redistribuicao`, corpo ? { ...corpo, motivo: undefined } : null);
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/redistribuicao`, aoSalvar);
  const pronto = Boolean(pv.previa) && pv.previa!.bases.fecha && !pv.previa!.escolhaPendente.length && motivo.trim().length >= 10;
  return (
    <Modal
      titulo={`Redistribuir parcelas — ${f.numero} · ${f.documento ?? f.pedido}`}
      aoFechar={aoFechar}
      largo
      rodape={<RodapeDistribuicao pv={pv.previa} carregando={pv.carregando} erro={pv.erro} motivo={motivo} aoMotivo={setMotivo} aoFechar={aoFechar} rotulo="Redistribuir" pronto={pronto} enviando={env.enviando} aoConfirmar={() => void env.enviar(corpo!)} teste="redistribuicao" />}
    >
      <Erro texto={env.erro} />
      <Bloqueio f={f} />
      <div className="grade-form tres">
        <Campo rotulo="Origem">
          {(id) => (
            <select id={id} value={origem} onChange={(e) => setOrigem(e.target.value)} data-teste="origem-redistribuicao">
              {ORIGENS_REDISTRIBUICAO.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <CampoData rotulo="Data da informação" valor={dataInfo} aoMudar={setDataInfo} max={hojeIso()} />
      </div>
      <h3 className="rc-subtitulo">
        Novas parcelas do Cliente (só as sem nenhum fato)
        <Ajuda titulo="Redistribuição">Parcelas liberadas, em nota da Inttrix, com resgate, valor confirmado, pagamento do Cliente ou reconhecimento ficam protegidas. A comissão do que sobra é redistribuída proporcionalmente às novas bases, com o resíduo na última; a soma das bases precisa fechar com o valor faturado da NF. A distribuição anterior fica no histórico.</Ajuda>
      </h3>
      <div className="rc-parcelas">
        {parcelas.map((p, n) => (
          <div key={n} className="rc-parcela">
            <Campo rotulo={`Parcela ${n + 1}: valor`}>{(id) => <input id={id} inputMode="decimal" value={p.base} onChange={(e) => setParcelas(parcelas.map((x, k) => (k === n ? { ...x, base: e.target.value } : x)))} data-teste={`parcela-valor-${n + 1}`} />}</Campo>
            <CampoData rotulo="Vencimento" valor={p.vencimento} aoMudar={(v) => setParcelas(parcelas.map((x, k) => (k === n ? { ...x, vencimento: v } : x)))} />
            <button type="button" className="botao-link" disabled={parcelas.length === 1} onClick={() => setParcelas(parcelas.filter((_, k) => k !== n))}>
              Remover
            </button>
          </div>
        ))}
        <button type="button" className="botao-secundario" onClick={() => setParcelas([...parcelas, { base: '', vencimento: '' }])} data-teste="adicionar-parcela">
          Adicionar parcela
        </button>
      </div>
      <Erro texto={pv.erro} />
      {pv.previa && (
        <>
          <TabelaDistribuicao pv={pv.previa} />
          <EscolhaPrazos pv={pv.previa} valor={tratamentos} aoMudar={setTratamentos} />
          {pv.previa.efeitosFatos.length > 0 && (
            <div className="aviso" data-teste="efeitos-fatos">
              {pv.previa.efeitosFatos.map((t) => (
                <div key={t}>{t}</div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

interface LinhaCal {
  prazo: string;
  vencimento: string;
  /** vencimento que a sugestão trouxe (linha vinda da sugestão); a edição manual fica registrada contra ele */
  sugerido: string | null;
  valor: string;
  origemValor: ParcelaCalendario['origemValor'];
}
const valorBr = (v: string | null) => (v === null ? '' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const somarDiasIso = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const somarMesesIso = (iso: string, n: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  const alvo = new Date(Date.UTC(a, m - 1 + n, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth(), Math.min(d, ultimo))).toISOString().slice(0, 10);
};
/** Divisão igual (resíduo na última): SUGESTÃO, marcada como tal — os valores documentados entram como estão. */
const dividirIgual = (totalCent: number, n: number) => Array.from({ length: n }, (_, i) => (i === n - 1 ? totalCent - Math.floor(totalCent / n) * (n - 1) : Math.floor(totalCent / n)));

/**
 * E0 — calendário do Cliente pela LISTA EXATA de parcelas (prazo em dias da NF, vencimento, valor e origem do valor):
 * condições irregulares como 30/45/90 ficam como são; "periodicidade fixa" só gera a lista para acordos periódicos.
 * Divisão igual é sugestão e exige conferência. Modo "complemento": parcela única com fato — só o vencimento.
 */
function InformarCalendario({ f, modo, aoFechar, aoSalvar }: { f: Ficha; modo: ModoCalendario; aoFechar: () => void; aoSalvar: () => void }) {
  const k = f.calendario!;
  const dg = f.diagnosticoCalendario;
  const complemento = modo === 'complemento';
  const sug = modo === 'sugestao' ? k.sugestao : null;
  // referência da soma: o que ainda não tem fato (sem fatos = valor faturado); no complemento, a base da parcela única
  const livreCent = complemento ? centavosDe(valorBr(dg.valorParcelaUnica)) : dg.parcelas.filter((x) => !x.motivos.length).reduce((s2, x) => s2 + centavosDe(valorBr(x.base)), 0);
  const [motivo, setMotivo] = useState('');
  const [origem, setOrigem] = useState<string>(sug?.fonte ?? '');
  const [documento, setDocumento] = useState(sug?.documento ?? '');
  const [linhas, setLinhas] = useState<LinhaCal[]>(
    sug
      ? sug.parcelas.map((x) => ({ prazo: x.prazoDias === null ? '' : String(x.prazoDias), vencimento: x.vencimento, sugerido: x.vencimento, valor: valorBr(x.valor), origemValor: x.origemValor }))
      : [{ prazo: '', vencimento: '', sugerido: null, valor: complemento ? valorBr(dg.valorParcelaUnica) : '', origemValor: 'documentado' }],
  );
  const [conferido, setConferido] = useState(false);
  const [tratamentos, setTratamentos] = useState<Tratamentos>({});
  const [gerador, setGerador] = useState({ quantidade: '', primeiro: '', periodicidade: 'mensal' as PeriodicidadeCalendario, intervalo: '30' });
  const mudar = (i: number, n: Partial<LinhaCal>) => setLinhas(linhas.map((l, j) => (j === i ? { ...l, ...n } : l)));
  const mudarPrazo = (i: number, prazo: string) => {
    const d = prazo.replace(/\D/g, '');
    mudar(i, { prazo: d, ...(k.dataNf && d !== '' ? { vencimento: somarDiasIso(k.dataNf, Number(d)) } : {}) });
  };
  // o vencimento escolhido (digitado ou no calendário) fica como está; o prazo passa a ser os dias corridos da NF até ele
  // (sem NF, ou vencimento antes da NF, o prazo fica vazio: vale só o vencimento)
  const mudarVencimento = (i: number, vencimento: string) => {
    if (!vencimento) return mudar(i, { vencimento });
    const dias = k.dataNf ? diasEntre(k.dataNf, vencimento) : -1;
    mudar(i, { vencimento, prazo: dias >= 0 && dias <= 3650 ? String(dias) : '' });
  };
  const igualar = (ls: LinhaCal[]) => {
    const vs = dividirIgual(livreCent, ls.length);
    return ls.map((l, i) => ({ ...l, valor: valorBr((vs[i] / 100).toFixed(2)), origemValor: 'sugestao_igual' as const }));
  };
  const gerar = () => {
    const q = Number(gerador.quantidade);
    if (!Number.isInteger(q) || q < 1 || q > 60 || !gerador.primeiro) return;
    const ls = Array.from({ length: q }, (_, i) => ({
      prazo: '',
      sugerido: null,
      vencimento: gerador.periodicidade === 'mensal' ? somarMesesIso(gerador.primeiro, i) : somarDiasIso(gerador.primeiro, i * Number(gerador.intervalo || 30)),
      valor: '',
      origemValor: 'documentado' as const,
    }));
    setLinhas(igualar(ls));
  };
  const temSugestao = linhas.some((l) => l.origemValor === 'sugestao_igual');
  const somaCent = linhas.reduce((s2, l) => s2 + (centavosDe(l.valor) || 0), 0);
  const validos = Boolean(origem) && documento.trim().length >= 3 && linhas.length >= 1 && linhas.every((l) => l.vencimento && centavosDe(l.valor) > 0);
  const corpo = validos
    ? {
        motivo: motivo || undefined,
        origem,
        documento,
        forma: 'lista',
        parcelas: linhas.map((l) => ({ prazo_dias: l.prazo === '' ? null : Number(l.prazo), vencimento: l.vencimento, vencimento_sugerido: l.sugerido ?? undefined, valor: (centavosDe(l.valor) / 100).toFixed(2), origem_valor: l.origemValor })),
        conferido,
        prazos_negociados: tratamentosDoCorpo(tratamentos),
      }
    : null;
  const pv = usePrevia<PreviaDistribuicao>(`/api/receber/comissoes/${f.numero}/calendario`, corpo ? { ...corpo, motivo: undefined, conferido: undefined } : null);
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/calendario`, aoSalvar);
  const pronto = Boolean(pv.previa) && pv.previa!.bases.fecha && !pv.previa!.escolhaPendente.length && motivo.trim().length >= 10 && (!temSugestao || conferido);
  return (
    <Modal
      titulo={`${complemento ? 'Completar o vencimento da parcela' : 'Informar o calendário do Cliente'} — ${f.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={<RodapeDistribuicao pv={pv.previa} carregando={pv.carregando} erro={pv.erro} motivo={motivo} aoMotivo={setMotivo} aoFechar={aoFechar} rotulo={complemento ? 'Gravar vencimento' : 'Gravar calendário'} pronto={pronto} enviando={env.enviando} aoConfirmar={() => void env.enviar(corpo!)} teste="calendario" />}
    >
      <Erro texto={env.erro} />
      <Bloqueio f={f} />
      <p className="texto-secundario sem-margem">
        Condição da operação: {k.condicao || '—'}
        {k.valorFaturado ? ` · valor faturado ${rs(k.valorFaturado)}` : ''}
        {k.dataNf ? ` · NF de ${dataBr(k.dataNf)}` : ''}. O texto da condição sozinho não basta: informe a origem.
      </p>
      {complemento && (
        <div className="aviso aviso-atencao-fin" data-teste="aviso-complemento">
          {dg.mensagem} Use só se o acordo com o Cliente for de uma parcela.
        </div>
      )}
      {sug && (
        <div className="rc-sugestao-cal" data-teste="sugestao-aplicada">
          Preenchido com a sugestão: <strong>{sug.rotulo}</strong> ({sug.documento}). Confira antes de gravar: nada é gravado sem confirmar.
          {sug.aviso && <span className="rc-sec rc-alerta-txt">{sug.aviso}</span>}
        </div>
      )}
      <div className="grade-form tres rc-calendario-campos">
        <Campo rotulo="Origem do calendário *">
          {(id) => (
            <select id={id} value={origem} onChange={(e) => setOrigem(e.target.value)} data-teste="origem-calendario">
              <option value="">Escolha…</option>
              {ORIGENS_CALENDARIO.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Documento ou referência *">{(id) => <input id={id} value={documento} maxLength={300} onChange={(e) => setDocumento(e.target.value)} data-teste="documento-calendario" />}</Campo>
        {!complemento && (
          <div className="rc-gerador" data-teste="gerador-periodicidade">
            <span className="rc-sec">Acordo periódico? Gere a lista:</span>
            <input aria-label="Quantidade de parcelas" placeholder="qtd." inputMode="numeric" value={gerador.quantidade} onChange={(e) => setGerador({ ...gerador, quantidade: e.target.value.replace(/\D/g, '') })} data-teste="quantidade-calendario" />
            <select aria-label="Periodicidade" value={gerador.periodicidade} onChange={(e) => setGerador({ ...gerador, periodicidade: e.target.value as PeriodicidadeCalendario })} data-teste="periodicidade-calendario">
              {PERIODICIDADES_CALENDARIO.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
            <CampoData rotulo="1º vencimento" valor={gerador.primeiro} aoMudar={(d) => setGerador({ ...gerador, primeiro: d })} teste="primeiro-vencimento" />
            <button type="button" className="botao-link" onClick={gerar} data-teste="gerar-lista">
              Gerar lista
            </button>
          </div>
        )}
      </div>
      <RolagemIndicada className="rolagem quadro-tabela rc-editor-cal" teste="editor-calendario">
        <table className="tabela tabela-compacta rc-fixo" data-teste="parcelas-calendario">
          <thead>
            <tr>
              <th style={{ width: '9%' }}>Parcela</th>
              <th style={{ width: '13%' }}>Prazo (dias da NF)</th>
              <th style={{ width: '24%' }}>Vencimento *</th>
              <th className="numero" style={{ width: '17%' }}>
                Valor *
              </th>
              <th style={{ width: '27%' }}>Origem do valor</th>
              <th style={{ width: '10%' }} />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i} data-teste="linha-calendario">
                <td>{i + 1}</td>
                <td>
                  <input aria-label={`Prazo da parcela ${i + 1}`} inputMode="numeric" value={l.prazo} disabled={!k.dataNf} onChange={(e) => mudarPrazo(i, e.target.value)} data-teste={`prazo-${i + 1}`} />
                </td>
                <td>
                  <CampoData rotulo={`Vencimento da parcela ${i + 1}`} classe="rc-data-linha" valor={l.vencimento} aoMudar={(d) => mudarVencimento(i, d)} teste={`vencimento-${i + 1}`} />
                  {l.sugerido && l.vencimento && l.vencimento !== l.sugerido && (
                    <span className="rc-sec" data-teste={`editado-${i + 1}`}>
                      editado · sugestão {dataBr(l.sugerido)}
                    </span>
                  )}
                </td>
                <td className="numero">
                  <input aria-label={`Valor da parcela ${i + 1}`} className="numero" value={l.valor} readOnly={complemento} onChange={(e) => mudar(i, { valor: e.target.value, origemValor: 'documentado' })} data-teste={`valor-${i + 1}`} />
                </td>
                <td>
                  {complemento ? (
                    <span className="rc-sec">base da parcela (não muda)</span>
                  ) : (
                    <select aria-label={`Origem do valor da parcela ${i + 1}`} value={l.origemValor} onChange={(e) => mudar(i, { origemValor: e.target.value as ParcelaCalendario['origemValor'] })} data-teste={`origem-valor-${i + 1}`}>
                      {ORIGENS_VALOR_CALENDARIO.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.nome}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {!complemento && linhas.length > 1 && (
                    <button type="button" className="botao-link" onClick={() => setLinhas(linhas.filter((_, j) => j !== i))} aria-label={`Remover a parcela ${i + 1}`}>
                      remover
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="rc-total">
              <td colSpan={3}>
                Soma das parcelas {somaCent === livreCent ? '✓' : '✗'} precisa fechar com {rs((livreCent / 100).toFixed(2))}
              </td>
              <td className="numero" data-teste="soma-calendario">
                {rs((somaCent / 100).toFixed(2))}
              </td>
              <td colSpan={2}>
                {!complemento && (
                  <span className="rc-linha-botoes">
                    <button type="button" className="botao-link" onClick={() => setLinhas([...linhas, { prazo: '', vencimento: '', sugerido: null, valor: '', origemValor: 'documentado' }])} data-teste="mais-parcela">
                      + parcela
                    </button>
                    <button type="button" className="botao-link" onClick={() => setLinhas(igualar(linhas))} data-teste="dividir-igual">
                      dividir igualmente (sugestão)
                    </button>
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </RolagemIndicada>
      {temSugestao && (
        <label className="opcao rc-conferido" data-teste="conferido-calendario">
          <input type="checkbox" checked={conferido} onChange={(e) => setConferido(e.target.checked)} /> Conferi os valores por divisão igual com o documento informado (obrigatório para gravar). Se os valores documentados forem outros, digite-os.
        </label>
      )}
      <Erro texto={pv.erro} />
      {pv.previa && (
        <>
          <TabelaDistribuicao pv={pv.previa} />
          <EscolhaPrazos pv={pv.previa} valor={tratamentos} aoMudar={setTratamentos} />
          {pv.previa.efeitosFatos.length > 0 && (
            <div className="aviso" data-teste="efeitos-fatos">
              {pv.previa.efeitosFatos.map((t) => (
                <div key={t}>{t}</div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- replanejar previsão
function Replanejar({ f, item, aoFechar, aoSalvar }: { f: Ficha; item: string; aoFechar: () => void; aoSalvar: () => void }) {
  const i = f.itens.find((x) => x.id === item)!;
  const [modo, setModo] = useState<'negociado' | 'retorno_calculo'>('negociado');
  const [data, setData] = useState(i.previsao.atual ?? hojeIso());
  const [motivo, setMotivo] = useState('');
  const env = useEnviar(`/api/receber/comissoes/${f.numero}/itens/${item}/previsao`, aoSalvar);
  const pronto = motivo.trim().length >= 4 && (modo === 'retorno_calculo' || Boolean(data));
  return (
    <Modal
      titulo={`Prazo da previsão — ${f.numero} · item ${i.ordem}/${i.total}`}
      aoFechar={aoFechar}
      medio
      rodape={<Rodape aoFechar={aoFechar} rotulo={modo === 'negociado' ? 'Registrar prazo negociado' : 'Voltar à data calculada'} pronto={pronto} enviando={env.enviando} aoConfirmar={() => void env.enviar(modo === 'negociado' ? { previsao: data, motivo } : { modo, motivo })} teste="confirmar-replanejar" />}
    >
      <Erro texto={env.erro} />
      <dl className="pares-regra">
        <div>
          <dt>Original</dt>
          <dd>{i.previsao.original ? dataBr(i.previsao.original) : 'sem data'} (preservada)</dd>
        </div>
        <div>
          <dt>Calculada agora</dt>
          <dd>{i.previsao.calculada ? `${dataBr(i.previsao.calculada)} · ${i.previsao.projecao.fundamento}` : `sem dados (falta: ${i.previsao.projecao.falta.join('; ')})`}</dd>
        </div>
        <div>
          <dt>Prazo negociado</dt>
          <dd>{i.previsao.negociada ? `${dataBr(i.previsao.negociada.data)} · ${i.previsao.negociada.motivo}` : 'nenhum'}</dd>
        </div>
      </dl>
      <div className="rc-filtros">
        <label className="opcao">
          <input type="radio" checked={modo === 'negociado'} onChange={() => setModo('negociado')} data-teste="modo-negociado" /> Informar prazo negociado (prevalece sobre o cálculo até nova decisão)
        </label>
        {i.previsao.negociada && (
          <label className="opcao">
            <input type="radio" checked={modo === 'retorno_calculo'} onChange={() => setModo('retorno_calculo')} data-teste="modo-retorno" /> Voltar a usar a data calculada
          </label>
        )}
      </div>
      <div className="grade-form duas">
        {modo === 'negociado' && <CampoData rotulo="Prazo negociado" valor={data} aoMudar={setData} obrigatorio teste="data-negociada" />}
        <Campo rotulo="Motivo *">{(id) => <input id={id} value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} data-teste="motivo-replanejar" />}</Campo>
      </div>
      <p className="texto-secundario">Novos fatos (pagamento, reconhecimento, resgate, nota) atualizam só a data calculada; o prazo negociado não é trocado sozinho, e o efeito aparece ao lado.</p>
    </Modal>
  );
}

/** Ação com justificativa obrigatória (estorno do reconhecimento, cancelamento da solicitação de resgate). */
function ComJustificativa({ titulo, rotulo, caminho, aviso, aoFechar, aoSalvar, teste }: { titulo: string; rotulo: string; caminho: string; aviso: string; aoFechar: () => void; aoSalvar: () => void; teste: string }) {
  const [just, setJust] = useState('');
  const env = useEnviar(caminho, aoSalvar);
  return (
    <Modal titulo={titulo} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo={rotulo} pronto={just.trim().length >= 10} enviando={env.enviando} aoConfirmar={() => void env.enviar({ justificativa: just })} teste={`confirmar-${teste}`} />}>
      <Erro texto={env.erro} />
      <p className="texto-secundario">{aviso}</p>
      <Campo rotulo="Justificativa (obrigatória, mínimo 10 caracteres)">{(i) => <textarea id={i} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} data-teste={`justificativa-${teste}`} />}</Campo>
    </Modal>
  );
}

/**
 * Solicitação de resgate (RSG): fato da Inttrix, distinto da nota, com data efetiva; por item ou grupo de itens
 * liberados desta comissão. Nada vem marcado; a prévia mostra o efeito na data esperada.
 */
function SolicitarResgate({ f, aoFechar, aoSalvar }: { f: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const elegiveis = f.itens.filter((i) => i.liberacao.situacao === 'liberado' && !i.resgate && i.cobranca.situacao !== 'quitado');
  const [itens, setItens] = useState<string[]>([]);
  const [data, setData] = useState(hojeIso());
  const [meio, setMeio] = useState('');
  const [chave] = useState(novaChave);
  const corpo = itens.length && data ? { itens, data, meio: meio || undefined } : null;
  const pv = usePrevia<{ efeitos: { item: string; antes: string | null; depois: string | null; negociada: string | null; marco: string }[] }>('/api/receber/resgates', corpo);
  const env = useEnviar('/api/receber/resgates', aoSalvar);
  return (
    <Modal titulo={`Registrar solicitação de resgate — ${f.numero}`} aoFechar={aoFechar} medio rodape={<Rodape aoFechar={aoFechar} rotulo="Registrar solicitação" pronto={Boolean(pv.previa)} enviando={env.enviando} aoConfirmar={() => void env.enviar({ ...corpo!, chave_idempotencia: chave })} teste="confirmar-resgate" />}>
      <Erro texto={env.erro ?? pv.erro} />
      <p className="texto-secundario sem-margem">Solicitação feita pela Inttrix ao parceiro, com a data efetiva. É diferente da nota da Inttrix. Selecione um item ou um grupo de itens liberados.</p>
      <div className="rc-lista-marcar" data-teste="resgate-itens">
        {elegiveis.map((i) => (
          <label key={i.id} className="opcao">
            <input type="checkbox" checked={itens.includes(i.id)} onChange={(e) => setItens(e.target.checked ? [...itens, i.id] : itens.filter((x) => x !== i.id))} data-teste={`resgate-item-${i.ordem}`} /> {i.ordem}/{i.total} · {i.rotulo} · {valorOuConfirmar(i.confirmado?.valor ?? i.estimado)}
          </label>
        ))}
      </div>
      <div className="grade-form duas">
        <CampoData rotulo="Data da solicitação" valor={data} aoMudar={setData} max={hojeIso()} obrigatorio teste="data-resgate" />
        <Campo rotulo="Meio (opcional)">{(id) => <input id={id} value={meio} maxLength={120} placeholder="Ex.: e-mail ao parceiro, portal" onChange={(e) => setMeio(e.target.value)} data-teste="meio-resgate" />}</Campo>
      </div>
      {pv.previa && (
        <ul className="rc-hist" data-teste="efeito-resgate">
          {pv.previa.efeitos.map((e) => (
            <li key={e.item}>
              <span>{e.item}</span>
              <span>
                data calculada {e.antes ? dataBr(e.antes) : 'sem dados'} → {e.depois ? dataBr(e.depois) : 'sem dados'} · {e.marco}
                {e.negociada ? ` · prazo negociado ${dataBr(e.negociada)} continua valendo` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
